import { confirmDialog } from "../components/confirmHost.jsx";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { apiFetch } from "../lib/api.js";
import { Upload, FileText, RefreshCw, ChevronDown, ChevronRight, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, X, Trash2, Sparkles, Wallet, Activity, AlertTriangle, Calendar, List, BarChart3, SlidersHorizontal } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import { ListovaniMesice, Hlaska, Zalozky, RozbalovaciVyber, Modal, HlavickaOkna } from "../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, SkupinaPrehledu, Pilulka } from "../components/PrehledDlazdice.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { ComposedChart, Line, BarChart, Bar, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";
import InfoTip from "../components/InfoTip.jsx";
import { useApiData, fetchApi } from "../lib/api.js";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { fmt, fmtShort, formatDate } from "../lib/format.js";
import { parsePdfFile } from "../lib/pdfParser.js";
import CashflowModel from "./CashflowModel.jsx";

const MIN_MONTH = "2026-01";
const MONTH_NAMES = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čer", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"];
const MONTH_FULL = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(mk) {
  if (!mk) return "";
  const [y, m] = mk.split("-");
  return `${MONTH_FULL[parseInt(m, 10) - 1]} ${y}`;
}

// Mapovaní L1 kategorie → barva pro vizuální rozlišení (P&L breakdown a další)
const L1_COLOR = {
  "Revenue": { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", solid: "bg-emerald-600" },
  "Marketing & Sales": { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", solid: "bg-rose-600" },
  "General & Administration": { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", solid: "bg-blue-600" },
  "Production": { bg: "bg-brand-50", text: "text-brand-700", border: "border-brand-200", solid: "bg-brand-600" },
  "R&D & Systems": { bg: "bg-plum-50", text: "text-plum-700", border: "border-plum-200", solid: "bg-plum-600" },
  "Others & Backup": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", solid: "bg-amber-600" },
  "Loans & Investments": { bg: "bg-brand-50", text: "text-brand-700", border: "border-brand-200", solid: "bg-brand-600" },
  "Interní": { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300", solid: "bg-slate-600" },
  "Operating": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", solid: "bg-amber-600" },
  "Financial": { bg: "bg-brand-50", text: "text-brand-700", border: "border-brand-200", solid: "bg-brand-600" },
  "": { bg: "bg-slate-50", text: "text-slate-500", border: "border-slate-200", solid: "bg-slate-400" },
};

// Které L1 jsou expense (záporné), které revenue (kladné), které financování / přesun
// - income/expense → součást Operating Result
// - financing → externí financování (Loans, Investments) — NENÍ v Op Result, JE v Cash flow
// - transfer → přesun mezi vlastními účty (Interní) — NENÍ ani v Op Result ani v Cash flow
//   (jen se přesouvá z jedné kapsy do druhé, žádný nový/odejítý cash z firmy)
const L1_KIND = {
  "Revenue": "income",
  "Marketing & Sales": "expense",
  "General & Administration": "expense",
  "Production": "expense",
  "R&D & Systems": "expense",
  "Others & Backup": "expense",
  "Loans & Investments": "financing",
  "Interní": "transfer",
  "Operating": "expense",
  "Financial": "expense",
};

// Pořadí L1 ve všech zobrazeních (priorita nahoře)
const L1_ORDER = [
  "Revenue",
  "Marketing & Sales",
  "General & Administration",
  "Production",
  "R&D & Systems",
  "Others & Backup",
  "Loans & Investments",
  "Interní",
];

export default function Cashflow() {
  const [monthKey, setMonthKey] = useState(currentMonthKey());
  // 1.11.2026 (#188): záložka Model, jeden trvalý model cashflow (CashflowModel.jsx).
  // Volba záložky se pamatuje jen v tomhle prohlížeči.
  const [rezim, setRezim] = useState(() => {
    try { return localStorage.getItem("cashflow_rezim") === "model" ? "model" : "prehled"; } catch { return "prehled"; }
  });
  const prepniRezim = (k) => {
    setRezim(k);
    try { localStorage.setItem("cashflow_rezim", k); } catch { /* bez úložiště */ }
  };
  const [uploading, setUploading] = useState(false);
  const { blurred } = useDemoMode(); // global demo režim ze Sidebaru
  const [uploadStatus, setUploadStatus] = useState(null);
  // Floating toast — pro hlášky co se musí objevit kdekoli na stránce (auto-klasifikace,
  // hromadné akce, apod.). Auto-dismiss po 4 s. Odděleno od uploadStatus kvůli UX: user
  // se při klasifikaci dívá dolů na transakce, nahoru ke statusu neuvidí.
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  // Default: zobrazujeme jen pending (klasifikované zmizí ze seznamu — uvidíš je v P&L vpravo)
  const [showOnlyPending, setShowOnlyPending] = useState(true);
  const fileInputRef = useRef(null);

  const dataUrl = `/api/cashflow?action=list&month=${monthKey}`;
  const { data, setData, loading, error, refreshing, refetch } = useApiData(dataUrl);

  // Auto-pull nových transakcí z Gmail emailu při mount + manuálním Obnovit.
  // Debounce: jen pokud poslední pull byl > 10 minut zpět (ať to nezdržuje při klikání).
  const [emailPulling, setEmailPulling] = useState(false);
  const [reapplying, setReapplying] = useState(false);
  const pullEmailTransactions = React.useCallback(async (force = false) => {
    try {
      const lastPullKey = "cashflow_last_email_pull";
      const lastPull = parseInt(localStorage.getItem(lastPullKey) || "0", 10);
      const tenMinAgo = Date.now() - 10 * 60 * 1000;
      if (!force && lastPull > tenMinAgo) return; // skip, nedávno proběhlo
      setEmailPulling(true);
      await apiFetch("/api/cashflow-parse-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days: 7 }),
      });
      localStorage.setItem(lastPullKey, String(Date.now()));
      await refetch();
    } catch (e) {
      // Silent — uživatel uvidí jen co už bylo v databázi, parser je background
      console.warn("Email pull failed:", e?.message || e);
    } finally {
      setEmailPulling(false);
    }
  }, [refetch]);

  // Při mount stránky: spustit parser na pozadí (non-blocking, debounced 10 min)
  useEffect(() => {
    pullEmailTransactions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: catData } = useApiData("/api/cashflow?action=categories");
  const { data: rulesData, setData: setRulesData, refetch: refetchRules } = useApiData("/api/cashflow?action=rules");
  const { data: trendData, refetch: refetchTrend } = useApiData("/api/cashflow?action=trend&months=12");
  // Denní rozpad pro vybraný měsíc (grafik příjmů/výdajů den po dni)
  const { data: dailyData } = useApiData(`/api/cashflow?action=daily&month=${monthKey}`);
  // Pre-load všechny FAPI faktury pro daný rok jednou — instantní lookup v editoru
  const fapiYear = monthKey.substring(0, 4);
  const { data: fapiData } = useApiData(`/api/cashflow?action=fapiAllInvoices&year=${fapiYear}`);

  const transactions = data?.transactions || [];
  const summary = data?.summary || { income: 0, expense: 0, balance: 0, count: 0, pendingCount: 0 };
  const categories = catData?.categories || [];
  const rules = rulesData?.rules || [];
  const trend = trendData?.trend || [];
  const fapiInvoices = fapiData?.invoices || [];

  // PDF upload handler
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadStatus(null);
    try {
      let totalAdded = 0, totalSkipped = 0;
      const emptyFiles = []; // soubory z nichž parser nic nevytáhnul
      for (const file of files) {
        const txs = await parsePdfFile(file);
        if (txs.length === 0) {
          // Parser nic nenašel — uchováme debug info pro zobrazení
          emptyFiles.push({ fileName: file.name, meta: txs._meta });
          continue;
        }
        const res = await fetchApi("/api/cashflow", {
          method: "POST",
          body: JSON.stringify({ action: "saveTransactions", transactions: txs }),
        });
        totalAdded += res?.added || 0;
        totalSkipped += res?.skipped || 0;
      }
      setUploadStatus({ ok: true, added: totalAdded, skipped: totalSkipped, emptyFiles });
      await refetch();
    } catch (e) {
      setUploadStatus({ ok: false, error: e.message });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Klasifikace — OPTIMISTIC: změna v UI hned, backend volání na pozadí
  const handleClassify = async (txId, l1, l2, l3, createRule) => {
    // 1. OPTIMISTIC update — uživatel vidí změnu okamžitě
    setData((prev) => {
      if (!prev?.transactions) return prev;
      const txs = prev.transactions.map((t) =>
        t.id === txId ? { ...t, kategorie_l1: l1, kategorie_l2: l2, kategorie_l3: l3, klasif_zdroj: "manual" } : t
      );
      const wasPending = prev.transactions.some((t) => t.id === txId && !t.kategorie_l1);
      return {
        ...prev,
        transactions: txs,
        summary: { ...prev.summary, pendingCount: Math.max(0, prev.summary.pendingCount - (wasPending ? 1 : 0)) },
      };
    });
    // 2. Backend save (na pozadí, bez await blokování UI)
    try {
      await fetchApi("/api/cashflow", {
        method: "POST",
        body: JSON.stringify({ action: "classify", id: txId, kategorie_l1: l1, kategorie_l2: l2, kategorie_l3: l3, createRule }),
      });
      // 3. Po vytvoření JAKÉHOKOLIV pravidla retroaktivně aplikujeme na všechny
      // pending transakce napříč všemi měsíci (FACEBK rule → klasifikuje všechny FACEBK tx,
      // VS rule → všechny tx s tím VS, fapi_service → všechny faktury se stejnou službou).
      if (createRule) {
        try {
          const r = await fetchApi("/api/cashflow", {
            method: "POST",
            body: JSON.stringify({ action: "reapplyRules" }),
          });
          if (r?.updated > 0) {
            const n = r.updated;
            setToast({
              kind: "success",
              text: `Pravidlo aplikováno automaticky na ${n} ${n === 1 ? "další transakci" : n < 5 ? "další transakce" : "dalších transakcí"} se stejnou službou`,
            });
            await refetch();
          }
        } catch (e) { /* ignore */ }
        refetchRules();
      }
      refetchTrend();
    } catch (e) {
      // Backend selhal — vrať revert (refetch z databáze)
      console.error("Classify failed:", e);
      await refetch();
    }
  };

  // Manuální spuštění auto-klasifikace na všechny pending transakce — užitečné po
  // hromadném importu PDF/email, kdy pravidla už existují ale nebyla aplikována zpětně.
  const handleReapplyRules = async () => {
    if (reapplying) return;
    setReapplying(true);
    try {
      const r = await fetchApi("/api/cashflow", {
        method: "POST",
        body: JSON.stringify({ action: "reapplyRules" }),
      });
      const n = r?.updated || 0;
      if (n > 0) {
        setToast({
          kind: "success",
          text: `Pravidla aplikována na ${n} ${n === 1 ? "transakci" : n < 5 ? "transakce" : "transakcí"}`,
        });
        await refetch();
        refetchTrend();
      } else {
        setToast({
          kind: "info",
          text: "Žádná pending transakce nevyhovuje existujícím pravidlům, klasifikuj ručně, aby se vytvořila nová pravidla.",
        });
      }
    } catch (e) {
      setToast({ kind: "error", text: `Aplikace pravidel selhala: ${e.message}` });
    } finally {
      setReapplying(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!await confirmDialog("Zrušit toto pravidlo? Transakce klasifikované přes něj zůstanou (můžeš je reklasifikovat ručně).")) return;
    // OPTIMISTIC — odstraň lokálně hned
    setRulesData((prev) => prev ? { ...prev, rules: (prev.rules || []).filter((r) => r.id !== ruleId) } : prev);
    try {
      await fetchApi("/api/cashflow", {
        method: "POST",
        body: JSON.stringify({ action: "deleteRule", ruleId }),
      });
    } catch (e) {
      console.error("DeleteRule failed:", e);
      await refetchRules(); // revert
    }
  };

  // === Bulk delete pravidel — selected ids ===
  const [selectedRuleIds, setSelectedRuleIds] = useState(() => new Set());
  const toggleRuleSelected = (id) => {
    setSelectedRuleIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const handleBulkDeleteRules = async () => {
    if (selectedRuleIds.size === 0) return;
    if (!await confirmDialog(`Smazat ${selectedRuleIds.size} ${selectedRuleIds.size === 1 ? "pravidlo" : selectedRuleIds.size < 5 ? "pravidla" : "pravidel"}? Transakce klasifikované přes ně zůstanou.`)) return;
    const ids = Array.from(selectedRuleIds);
    // OPTIMISTIC — odstraň lokálně hned
    setRulesData((prev) => prev ? { ...prev, rules: (prev.rules || []).filter((r) => !ids.includes(r.id)) } : prev);
    setSelectedRuleIds(new Set());
    try {
      await fetchApi("/api/cashflow", {
        method: "POST",
        body: JSON.stringify({ action: "bulkDeleteRules", ruleIds: ids }),
      });
    } catch (e) {
      console.error("BulkDelete failed:", e);
      await refetchRules(); // revert
    }
  };

  // Reset modal — krásnější než nativní confirm/prompt, dvoukrokové potvrzení v stylu appky
  const [resetModalStep, setResetModalStep] = useState(0); // 0=zavřeno, 1=varování+výběr, 2=type-to-confirm
  // Rozsah resetu — "all" (vše) nebo konkrétní měsíc "YYYY-MM"
  const [resetScope, setResetScope] = useState("all");
  const handleResetAll = () => {
    setResetScope("all");
    setResetModalStep(1);
  };
  const performReset = async () => {
    const scope = resetScope; // capture aby nebyl race
    setResetModalStep(0);
    setUploading(true);
    try {
      const body = scope === "all"
        ? { action: "clearTransactions" }
        : { action: "clearTransactions", month: scope };
      const r = await fetchApi("/api/cashflow", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setUploadStatus({ ok: true, added: 0, skipped: 0, cleared: true });
      await refetch();
      refetchTrend();
      if (scope !== "all") {
        const n = r?.deleted ?? 0;
        setToast({ kind: "success", text: `Smazáno ${n} ${n === 1 ? "transakce" : n < 5 ? "transakce" : "transakcí"} za ${scope}.` });
      }
    } catch (e) {
      setUploadStatus({ ok: false, error: e.message });
    } finally {
      setUploading(false);
    }
  };

  // Lift up — který řádek je aktuálně rozbalený (jen 1 najednou)
  const [editingTxId, setEditingTxId] = useState(null);
  // Bulk select — sada vybraných tx ID + bulk klasifikace
  const [selectedTxIds, setSelectedTxIds] = useState(() => new Set());
  const [bulkEditing, setBulkEditing] = useState(false);
  // Filter podle směru transakce + podle kategorie L1
  const [txDirFilter, setTxDirFilter] = useState("all"); // all / kredit / debet
  const [txCatFilter, setTxCatFilter] = useState("all"); // all / "Revenue" / "Marketing & Sales" / ...

  // Filter — pending + směr + kategorie
  const visibleTransactions = useMemo(() => {
    let out = transactions;
    if (showOnlyPending) out = out.filter((t) => !t.kategorie_l1);
    if (txDirFilter !== "all") out = out.filter((t) => t.typ === txDirFilter);
    if (txCatFilter !== "all") {
      if (txCatFilter === "__pending__") out = out.filter((t) => !t.kategorie_l1);
      else out = out.filter((t) => t.kategorie_l1 === txCatFilter);
    }
    return out;
  }, [transactions, showOnlyPending, txDirFilter, txCatFilter]);

  // Counter pro kategorie chipy (z VŠECH transakcí, respektuje pending+direction filtry)
  const txCatCounts = useMemo(() => {
    let base = transactions;
    if (showOnlyPending) base = base.filter((t) => !t.kategorie_l1);
    if (txDirFilter !== "all") base = base.filter((t) => t.typ === txDirFilter);
    const counts = { all: base.length, __pending__: base.filter((t) => !t.kategorie_l1).length };
    for (const t of base) {
      const l1 = t.kategorie_l1;
      if (!l1) continue;
      counts[l1] = (counts[l1] || 0) + 1;
    }
    return counts;
  }, [transactions, showOnlyPending, txDirFilter]);

  // Counters pro filter chipy (respektují pending filter ale ne direction)
  const txDirCounts = useMemo(() => {
    const base = showOnlyPending ? transactions.filter((t) => !t.kategorie_l1) : transactions;
    return {
      all: base.length,
      kredit: base.filter((t) => t.typ === "kredit").length,
      debet: base.filter((t) => t.typ === "debet").length,
    };
  }, [transactions, showOnlyPending]);

  const toggleTxSelected = (id) => {
    setSelectedTxIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Vybrat vše / zrušit vše nad právě viditelnými transakcemi
  const vseTxVybrano = visibleTransactions.length > 0 && visibleTransactions.every((t) => selectedTxIds.has(t.id));
  const prepniVsechnyTx = () => {
    setSelectedTxIds(vseTxVybrano ? new Set() : new Set(visibleTransactions.map((t) => t.id)));
  };

  // Bulk klasifikace — aplikuje jednu kategorii na všechny vybrané, sekvenčně
  const handleBulkClassify = async (l1, l2, l3) => {
    const ids = Array.from(selectedTxIds);
    if (ids.length === 0) return;
    // Optimistic update
    setData((prev) => {
      if (!prev?.transactions) return prev;
      const idSet = new Set(ids);
      const wasPendingCount = prev.transactions.filter((t) => idSet.has(t.id) && !t.kategorie_l1).length;
      return {
        ...prev,
        transactions: prev.transactions.map((t) =>
          idSet.has(t.id) ? { ...t, kategorie_l1: l1, kategorie_l2: l2, kategorie_l3: l3, klasif_zdroj: "manual" } : t
        ),
        summary: { ...prev.summary, pendingCount: Math.max(0, prev.summary.pendingCount - wasPendingCount) },
      };
    });
    setSelectedTxIds(new Set());
    setBulkEditing(false);
    // Backend sekvenčně (bez pravidla — bulk action není o vytváření pravidel)
    let ok = 0;
    for (const id of ids) {
      try {
        await fetchApi("/api/cashflow", {
          method: "POST",
          body: JSON.stringify({ action: "classify", id, kategorie_l1: l1, kategorie_l2: l2, kategorie_l3: l3, createRule: null }),
        });
        ok++;
      } catch (e) { console.error("bulk classify failed for", id, e); }
    }
    setToast({
      kind: "success",
      text: `Hromadně klasifikováno ${ok} ${ok === 1 ? "transakce" : ok < 5 ? "transakce" : "transakcí"}`,
    });
    refetchTrend();
  };

  // P&L hierarchický strom: L1 → L2 → L3 s totals
  const plTree = useMemo(() => {
    const tree = {}; // { l1: { total, count, l2s: { l2name: { total, count, l3s: {l3: {total, count}} } } } }
    transactions.forEach((t) => {
      if (!t.kategorie_l1 && !t.kategorie_l2) return;
      const l1 = t.kategorie_l1 || "Nezařazeno";
      const l2 = t.kategorie_l2 || "—";
      const l3 = t.kategorie_l3 || "";
      const amt = parseFloat(String(t.castka_czk).replace(/[ \u00a0]/g, "").replace(",", ".")) || 0;
      const signed = t.typ === "kredit" ? amt : -amt;
      if (!tree[l1]) tree[l1] = { l1, total: 0, count: 0, l2s: {} };
      tree[l1].total += signed;
      tree[l1].count++;
      if (!tree[l1].l2s[l2]) tree[l1].l2s[l2] = { l2, total: 0, count: 0, l3s: {}, txs: [] };
      tree[l1].l2s[l2].total += signed;
      tree[l1].l2s[l2].count++;
      tree[l1].l2s[l2].txs.push(t); // jednotlivé transakce pro rozbalení + přetagování v breakdownu
      const l3key = l3 || "(bez detailu)";
      if (!tree[l1].l2s[l2].l3s[l3key]) tree[l1].l2s[l2].l3s[l3key] = { l3: l3key, total: 0, count: 0 };
      tree[l1].l2s[l2].l3s[l3key].total += signed;
      tree[l1].l2s[l2].l3s[l3key].count++;
    });
    // Vrátíme pole L1 v daném pořadí (Revenue, M&S, G&A, ...)
    const ordered = [];
    for (const l1Name of L1_ORDER) {
      if (tree[l1Name]) ordered.push(tree[l1Name]);
    }
    // Plus zbylé L1 co nejsou v L1_ORDER
    for (const l1Name of Object.keys(tree)) {
      if (!L1_ORDER.includes(l1Name)) ordered.push(tree[l1Name]);
    }
    return ordered;
  }, [transactions]);

  // Největší skupina v rozpadu, podle ní se škáluje proužek podílu u ostatních.
  const plMaxTotal = useMemo(
    () => plTree.reduce((max, g) => Math.max(max, Math.abs(g.total)), 0),
    [plTree]
  );

  // L1 overview pro horní pruh — totals, max abs hodnota pro škálování barů
  const l1Overview = useMemo(() => {
    const items = plTree.map((g) => ({
      l1: g.l1,
      total: g.total,
      count: g.count,
      kind: L1_KIND[g.l1] || "neutral",
    }));
    const maxAbs = items.reduce((m, i) => Math.max(m, Math.abs(i.total)), 0);
    return { items, maxAbs };
  }, [plTree]);

  // Souhrnné totals pro top KPI
  // - revenue / expenses → operativa (Revenue L1, expense L1 skupiny)
  // - saldo (Operating Result) = revenue + expenses
  // - cashFlow = saldo + financing (půjčky/investice) — BEZ Interní (přesuny vlastních účtů
  //   nepřinášejí ani neodvádějí cash z firmy)
  const topTotals = useMemo(() => {
    let rev = 0, exp = 0, financingFlow = 0, internalFlow = 0;
    for (const g of plTree) {
      const kind = L1_KIND[g.l1];
      if (kind === "income") rev += g.total;
      else if (kind === "expense") exp += g.total;
      else if (kind === "financing") financingFlow += g.total;
      else if (kind === "transfer") internalFlow += g.total;
    }
    const saldo = rev + exp;
    return {
      revenue: rev,
      expenses: exp,
      saldo,
      financingFlow,
      internalFlow,
      neutralFlow: financingFlow, // backward-compat — Cash flow brand neutralFlow ale jen financing
      cashFlow: saldo + financingFlow, // Interní převody se nezapočítávají
    };
  }, [plTree]);

  // === CEO METRIKY: MoM delta, EOM projekce, cash runway, anomálie ===
  const ceoMetrics = useMemo(() => {
    const m = {
      momRevenue: null, momExpenses: null, momOpResult: null,
      eomRevenue: null, eomExpenses: null, eomOpResult: null,
      avgMonthlyBurn: null, runwayMonths: null,
      isCurrentMonth: false, daysElapsed: 0, daysInMonth: 30, monthProgress: 1,
      prevMonth: null, monthsBack3: [],
    };
    if (trend.length === 0) return m;

    // Najdi index aktuálního zobrazeného měsíce v trendu
    const idx = trend.findIndex(t => t.mk === monthKey);
    const cur = idx >= 0 ? trend[idx] : null;
    const prev = idx > 0 ? trend[idx - 1] : null;
    m.prevMonth = prev;

    if (prev) {
      const r = topTotals.revenue, e = Math.abs(topTotals.expenses), s = topTotals.saldo;
      const pr = prev.revenue, pe = Math.abs(prev.expenses), ps = prev.opResult;
      m.momRevenue = pr > 0 ? Math.round(((r - pr) / pr) * 100) : null;
      m.momExpenses = pe > 0 ? Math.round(((e - pe) / pe) * 100) : null;
      m.momOpResult = ps !== 0 ? Math.round(((s - ps) / Math.abs(ps)) * 100) : null;
    }

    // Je zobrazený měsíc aktuální?
    const today = new Date();
    const todayMk = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    m.isCurrentMonth = monthKey === todayMk;

    // Dny v měsíci + uplynulé
    const [yy, mm] = monthKey.split("-").map(Number);
    m.daysInMonth = new Date(yy, mm, 0).getDate();
    if (m.isCurrentMonth) {
      m.daysElapsed = today.getDate();
    } else {
      m.daysElapsed = m.daysInMonth; // neaktivní měsíc — celý
    }
    m.monthProgress = m.daysElapsed / m.daysInMonth;

    // Projekce konec měsíce (jen pro current month) — lineární extrapolace
    if (m.isCurrentMonth && m.daysElapsed > 2) {
      const factor = m.daysInMonth / m.daysElapsed;
      m.eomRevenue = Math.round(topTotals.revenue * factor);
      m.eomExpenses = Math.round(topTotals.expenses * factor);
      m.eomOpResult = Math.round(topTotals.saldo * factor);
    }

    // Cash runway: průměrný měsíční burn za posledních 3 dokončené měsíce
    const completedMonths = trend.filter(t => t.mk < todayMk);
    const last3 = completedMonths.slice(-3);
    m.monthsBack3 = last3;
    if (last3.length >= 2) {
      const avgBurn = last3.reduce((s, x) => s + Math.abs(Math.min(0, x.opResult)), 0) / last3.length;
      m.avgMonthlyBurn = Math.round(avgBurn);
      // Bez startovacího cash zůstatku runway nemůžeme přesně. Uvedeme jen průměrný burn.
    }

    return m;
  }, [trend, monthKey, topTotals]);

  // === ANOMÁLIE pro CEO alert pásek ===
  const anomalies = useMemo(() => {
    const out = [];
    if (!ceoMetrics.prevMonth) return out;

    // Operating Result spadl o ≥ 20% MoM
    if (ceoMetrics.momOpResult !== null && ceoMetrics.momOpResult <= -20) {
      out.push({
        kind: "danger",
        icon: TrendingDown,
        text: `Operating Result spadl o ${Math.abs(ceoMetrics.momOpResult)}% vs minulý měsíc`,
      });
    }

    // Revenue klesá MoM o víc jak 15%
    if (ceoMetrics.momRevenue !== null && ceoMetrics.momRevenue <= -15) {
      out.push({
        kind: "warning",
        icon: TrendingDown,
        text: `Příjmy poklesly o ${Math.abs(ceoMetrics.momRevenue)}% vs minulý měsíc`,
      });
    }

    // Výdaje rostou o víc jak 25%
    if (ceoMetrics.momExpenses !== null && ceoMetrics.momExpenses >= 25) {
      out.push({
        kind: "warning",
        icon: TrendingUp,
        text: `Výdaje vyrostly o ${ceoMetrics.momExpenses}% vs minulý měsíc`,
      });
    }

    // Vysoký podíl pending klasifikace (>30 % transakcí)
    if (summary.count > 0 && summary.pendingCount / summary.count > 0.3) {
      out.push({
        kind: "info",
        icon: AlertCircle,
        text: `${summary.pendingCount} transakcí (${Math.round((summary.pendingCount / summary.count) * 100)}%) čeká na klasifikaci, bez nich je P&L nepřesné`,
      });
    }

    // Marketing & Sales výrazně nad průměrem 3 měsíců
    if (ceoMetrics.monthsBack3.length >= 2) {
      const curMS = (plTree.find(g => g.l1 === "Marketing & Sales")?.total || 0);
      const avgMS = ceoMetrics.monthsBack3.reduce((s, m) => s + ((m.l1 && m.l1["Marketing & Sales"]) || 0), 0) / ceoMetrics.monthsBack3.length;
      if (avgMS < 0 && Math.abs(curMS) > Math.abs(avgMS) * 1.4) {
        const pct = Math.round((Math.abs(curMS) / Math.abs(avgMS) - 1) * 100);
        out.push({
          kind: "warning",
          icon: AlertTriangle,
          text: `Marketing & Sales je ${pct}% nad průměrem 3 měsíců`,
        });
      }
    }

    return out;
  }, [ceoMetrics, plTree, summary]);

  // Trend data pro mini sparkline (last 6 měsíců, lokálně formátovaná)
  const sparkData = useMemo(() => {
    return trend.slice(-6).map(t => ({
      mk: t.mk,
      label: MONTH_NAMES[parseInt(t.mk.split("-")[1], 10) - 1],
      revenue: t.revenue,
      expenses: -t.expenses, // záporné → kladné pro graf
      opResult: t.opResult,
      cashFlow: t.cashFlow != null ? t.cashFlow : t.opResult,
      neutralFlow: t.neutralFlow || 0,
    }));
  }, [trend]);

  // === YTD PŘEHLED ROKU + projekce na konec roku ===
  // Kumulativní součet Revenue / Expenses / Op Result / Cash flow od 1. ledna aktuálního roku
  // do konce měsíce, který se právě prohlíží (nebo do dneška, pokud je to aktuální měsíc).
  // Plus EoY projekce (lineární extrapolace) + průměr na měsíc.
  const yearTotals = useMemo(() => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMk = `${todayYear}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    // Použijeme rok ze zobrazeného měsíce (ne nutně aktuální — user může scrollovat)
    const [yyStr, mmStr] = monthKey.split("-");
    const yy = parseInt(yyStr, 10);
    const mm = parseInt(mmStr, 10);
    // Pokud user prohlíží budoucí měsíc, YTD bereme po aktuální měsíc (nezahrnout nefinalizované)
    const cutoffMk = monthKey > todayMk ? todayMk : monthKey;
    const ytdMonths = trend.filter(t => t.mk.startsWith(yyStr) && t.mk <= cutoffMk);
    if (ytdMonths.length === 0) return null;
    let rev = 0, exp = 0, op = 0, cf = 0;
    for (const m of ytdMonths) {
      rev += m.revenue;
      exp += m.expenses; // záporné
      op += m.opResult;
      cf += m.cashFlow != null ? m.cashFlow : m.opResult;
    }
    // Dny uplynulé v roce (do konce zobrazeného měsíce — nebo do dneška, pokud je aktuální)
    const yearStart = new Date(yy, 0, 1);
    const yearEnd = new Date(yy, 11, 31);
    const isCurrentMonth = monthKey === todayMk;
    const endDate = isCurrentMonth ? today : new Date(yy, mm, 0); // konec zobrazeného měsíce
    const daysElapsed = Math.max(1, Math.floor((endDate - yearStart) / 86400000) + 1);
    const daysInYear = Math.floor((yearEnd - yearStart) / 86400000) + 1;
    const factor = daysInYear / daysElapsed;
    return {
      ytdRevenue: rev,
      ytdExpenses: exp,
      ytdOpResult: op,
      ytdCashFlow: cf,
      eoyRevenue: Math.round(rev * factor),
      eoyExpenses: Math.round(exp * factor),
      eoyOpResult: Math.round(op * factor),
      eoyCashFlow: Math.round(cf * factor),
      avgMonthlyRevenue: Math.round(rev / ytdMonths.length),
      avgMonthlyExpenses: Math.round(exp / ytdMonths.length),
      avgMonthlyOpResult: Math.round(op / ytdMonths.length),
      avgMonthlyCashFlow: Math.round(cf / ytdMonths.length),
      daysElapsed,
      daysInYear,
      daysRemaining: daysInYear - daysElapsed,
      currentYear: yy,
      monthsYtd: ytdMonths.length,
    };
  }, [trend, monthKey]);

  return (
    <div className={`p-4 sm:p-6 space-y-6 ${blurred ? "demo-blur" : ""}`}>
      {/* === RESET MODAL — vlastní design místo nativního confirm/prompt === */}
      <ResetConfirmModal
        step={resetModalStep}
        scope={resetScope}
        setScope={setResetScope}
        availableMonths={trend.map((t) => ({ mk: t.mk, count: t.count || 0 }))}
        onClose={() => setResetModalStep(0)}
        onProceedToStep2={() => setResetModalStep(2)}
        onConfirm={performReset}
      />

      {/* === FLOATING TOAST — viditelný kdekoli na stránce (pro klasifikační hlášky) === */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-2">
          <div className={`flex items-center gap-2.5 pl-4 pr-3 py-3 rounded-xl shadow-2xl border-2 text-sm font-medium min-w-[280px] max-w-[90vw] ${
            toast.kind === "success"
              ? "bg-emerald-50 border-emerald-400 text-emerald-900"
              : toast.kind === "error"
                ? "bg-red-50 border-red-400 text-red-900"
                : "bg-slate-50 border-slate-400 text-slate-900"
          }`}>
            {toast.kind === "success" ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
            <span className="flex-1">{toast.text}</span>
            <button onClick={() => setToast(null)} className="p-1 rounded hover:bg-white/50">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Import bar přesunut těsně nad Transakce — je tam lepší context */}

      {/* === POHLED NA MĚSÍC === Refresh data je teď v této hlavičce vpravo, vedle šipek na měsíc */}
      {/* 13.9.2026 (fáze 4f): listování měsíci vypadlo z hlavičky ven na stránku,
          stejně jako ve Výplatách a v přehledech. V hlavičce byly jen šipky bez
          popisku, takže nebylo vidět, který měsíc je vybraný, dokud člověk
          nepřečetl nadpis. */}
      <GradientHeader
        title="Cashflow"
        icon={Wallet}
        loading={refreshing || emailPulling}
        napoveda="Transakce a součty pro vybraný měsíc. Měsíc se přepíná pod hlavičkou. Obnovit stáhne nové transakce z e-mailu a znovu načte data z databáze."
        akce={[
          { klic: "obnovit", ikona: RefreshCw, popis: "Stáhnout nové transakce z e-mailu a obnovit", onClick: () => pullEmailTransactions(true), disabled: refreshing || emailPulling },
        ]}
      />

      <Zalozky
        stitek="Zobrazit"
        aria-label="Režim stránky Cashflow"
        value={rezim}
        onChange={prepniRezim}
        items={[
          { key: "prehled", label: "Skutečnost" },
          { key: "model", label: "Model" },
        ]}
      />

      {rezim === "model" ? (
        <CashflowModel />
      ) : (
      <>
      <MonthPicker monthKey={monthKey} setMonthKey={setMonthKey} />

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">Chyba: {error}</div>}
      {loading && (
        <div className="flex items-center justify-center py-12 bg-white border border-slate-200 rounded-xl">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          <span className="ml-3 text-sm text-slate-500">Načítám…</span>
        </div>
      )}

      {!loading && (
        <>
          {/* === ANOMÁLIE / ALERTY === */}
          {anomalies.length > 0 && <AnomalyStrip anomalies={anomalies} />}

          {/* === CEO HERO: Operating Result velký + sparkline + projekce === */}
          <CeoHero
            topTotals={topTotals}
            ceoMetrics={ceoMetrics}
            sparkData={sparkData}
            pendingCount={summary.pendingCount}
            txCount={summary.count}
          />

          {/* === PŘEHLED ROKU YTD + projekce do konce roku === */}
          {yearTotals && <YearOverview yearTotals={yearTotals} monthKey={monthKey} />}

          {/* Rozpad podle skupin odstraněn — duplikoval P&L breakdown níž */}

          {/* === MĚSÍČNÍ CASHFLOW — denní rozpad pro vybraný měsíc === */}
          {dailyData?.ok && (
            <KartaPrehledu>
              <HlavickaDlazdice
                ikona={Activity}
                nazev="Průběh měsíce"
                odznak={monthLabel(monthKey)}
                napoveda="Denní rozpad příjmů a výdajů pro vybraný měsíc. Vidíš, v jaký den přišel velký příjem, kdy odešly velké výdaje a jak měsíc postupně narůstá do plusu nebo minusu."
              />
              <DailyCashflowChart data={dailyData.days} summary={dailyData.summary} blurred={blurred} />
            </KartaPrehledu>
          )}

          {/* === TREND posledních 6 měsíců === */}
          {sparkData.length >= 2 && (
            <KartaPrehledu>
              <HlavickaDlazdice
                ikona={TrendingUp}
                nazev="Trend měsíců"
                podtitulek={`posledních ${sparkData.length} měsíců`}
                napoveda="Vývoj příjmů, výdajů a provozního výsledku měsíc po měsíci. Ukazuje dlouhodobé tendence: růst, sezónnost a anomálie."
              />
              <div className="p-3 sm:p-4">
                <TrendChart data={sparkData} />
              </div>
            </KartaPrehledu>
          )}

          {/* 13.9.2026 (kousek 4/7): import je karta ze vzorníku a kreslí se až od
              počítače. Platby normálně přitečou samy z e-mailu, tohle je záloha
              pro případ, že je potřeba dotáhnout starší výpis, a PDF z banky se
              stahuje na počítači. */}
          <div className="hidden lg:block">
          <KartaPrehledu>
            <HlavickaDlazdice
              ikona={Upload}
              nazev="Import z banky"
              podtitulek="záloha, běžně přitečou platby z e-mailu"
              napoveda="Nahraj sem PDF výpisy z Raiffeisenbank, tedy měsíční Výpis nebo export Pohyby. Server PDF přečte, vytáhne transakce a rozřadí je podle data do správných měsíců. Můžeš nahrát víc souborů najednou, co už v databázi je, se znovu nepřidá, kontroluje se ID a hash."
            />
            <div className="flex flex-wrap items-center gap-2 p-3 sm:p-4">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn btn-primary"
                >
                  {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Importuji…" : "Vybrat PDF výpis"}
                </button>
                <input ref={fileInputRef} type="file" accept="application/pdf" multiple
                       className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                <button
                  onClick={handleResetAll}
                  disabled={uploading}
                  className="btn border border-rose-200 bg-rose-50 text-rose-700 sm:hover:bg-rose-100"
                  title="Smazat všechny transakce a začít znovu"
                >
                  <Trash2 className="h-4 w-4" /> Reset dat
                </button>
            </div>
            {/* Status uploadu — výsledek importu, debug pro empty files, chybové stavy */}
            {uploadStatus && (
              <div className={`mx-3 mb-3 rounded-xl p-2.5 text-sm sm:mx-4 sm:mb-4 ${uploadStatus.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"}`}>
                {uploadStatus.ok ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
                    {uploadStatus.cleared
                      ? "Transakce smazány (zůstaly v záloze). Teď nahraj PDF výpisy znovu, naimportují se s opravenými daty."
                      : `Importováno ${uploadStatus.added} nových transakcí, ${uploadStatus.skipped} duplikátů přeskočeno.`}
                    <button onClick={() => setUploadStatus(null)} className="float-right hover:bg-white/20 rounded p-0.5"><X className="w-4 h-4" /></button>

                    {uploadStatus.emptyFiles && uploadStatus.emptyFiles.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <div className="font-semibold text-amber-800 mb-1.5">
                          Parser nenašel žádné transakce v {uploadStatus.emptyFiles.length} {uploadStatus.emptyFiles.length === 1 ? "souboru" : "souborech"}.
                        </div>
                        <div className="text-xs text-slate-700 mb-2">
                          Níže je vzorek textu z PDF, zkopíruj ho a pošli Claudovi, aby upravil rozpoznávání formátu.
                        </div>
                        {uploadStatus.emptyFiles.map((ef, i) => (
                          <details key={i} className="mb-2 bg-white border border-amber-200 rounded">
                            <summary className="cursor-pointer p-2 text-xs font-mono text-slate-700 hover:bg-amber-50">
                              {ef.fileName}
                              {ef.meta && <span className="ml-2 text-slate-400">({ef.meta.pageCount} str., {ef.meta.textLength} znaků, {ef.meta.debug?.lineCount || "?"} řádků)</span>}
                            </summary>
                            <div className="p-2 space-y-2">
                              {ef.meta?.debug?.currencyLines?.length > 0 && (
                                <div>
                                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Řádky s částkou (CZK/EUR/USD):</div>
                                  <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap font-mono text-slate-800">
{ef.meta.debug.currencyLines.join("\n")}
                                  </pre>
                                </div>
                              )}
                              {ef.meta?.debug?.rawTextSample && (
                                <div>
                                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Vzorek raw textu (prvních ~4 kB):</div>
                                  <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-auto max-h-60 whitespace-pre-wrap font-mono text-slate-800">
{ef.meta.debug.rawTextSample}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </details>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 inline mr-1.5" />
                    Chyba: {uploadStatus.error}
                    {String(uploadStatus.error || "").toLowerCase().includes("worker") && (
                      <div className="mt-1.5 text-xs text-red-700">
                        Tip: udělej tvrdý refresh stránky (Ctrl/Cmd + Shift + R), v prohlížeči je nakešovaná stará verze.
                      </div>
                    )}
                    <button onClick={() => setUploadStatus(null)} className="float-right hover:bg-white/20 rounded p-0.5"><X className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            )}
          </KartaPrehledu>
          </div>

          {/* Transakce + P&L */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Tabulka transakcí */}
            <div className="lg:col-span-3">
              {/* 13.9.2026 (kousek 5/7, rozhodnutí Č1): seznam transakcí je karta ze
                  vzorníku. Dřív nad ním visely dvě řady barevných štítků, jedna na
                  směr a druhá na kategorii, a než se ukázala první transakce, byla
                  pryč půl obrazovky. Teď je Vše a K vyřízení přepínač jako jinde
                  a směr s kategorií jsou rozbalovačky se štítkem, takže unesou
                  i dvacet kategorií. Aplikovat pravidla je akce v hlavičce. */}
              <KartaPrehledu>
                <HlavickaDlazdice
                  ikona={List}
                  nazev="Transakce"
                  podtitulek={`${visibleTransactions.length} ${showOnlyPending ? "k zařazení" : "za měsíc"}`}
                  napoveda="Seznam všech bankovních pohybů za vybraný měsíc. Žluté řádky čekají na zařazení, klepni na řádek a vyber kategorii. Příjmy mají zelený plus, výdaje červený minus. Zaškrtávátky můžeš zařadit víc transakcí najednou."
                  akce={summary.pendingCount > 0 ? (
                    <button
                      type="button"
                      onClick={handleReapplyRules}
                      disabled={reapplying}
                      title="Aplikovat existující pravidla na všechny transakce k zařazení. Hodí se po importu nebo když chceš zpětně zařadit staré transakce nově vytvořenými pravidly."
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/25 bg-white/15 disabled:opacity-60 sm:hover:bg-white/25"
                    >
                      {reapplying ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                    </button>
                  ) : null}
                />

                {/* Filtry: přepínač stavu, pod ním směr a kategorie jako rozbalovačky */}
                <div className="space-y-2 border-b border-slate-200 bg-brand-light/60 p-3 sm:p-4">
                  <Zalozky
                    stitek="Zobrazit"
                    aria-label="Zobrazit transakce"
                    value={showOnlyPending ? "pending" : "vse"}
                    onChange={(k) => setShowOnlyPending(k === "pending")}
                    items={[
                      { key: "vse", label: `Vše (${summary.count})` },
                      { key: "pending", label: `K zařazení (${summary.pendingCount})` },
                    ]}
                  />
                  <RozbalovaciVyber
                    stitek="Směr"
                    aria-label="Filtr podle směru transakce"
                    value={txDirFilter}
                    onChange={setTxDirFilter}
                    items={[
                      { key: "all", label: `Všechny (${txDirCounts.all})` },
                      { key: "kredit", label: `Příjmy (${txDirCounts.kredit})` },
                      { key: "debet", label: `Výdaje (${txDirCounts.debet})` },
                    ]}
                  />
                  <RozbalovaciVyber
                    stitek="Kategorie"
                    aria-label="Filtr podle kategorie"
                    value={txCatFilter}
                    onChange={setTxCatFilter}
                    items={[
                      { key: "all", label: `Všechny (${txCatCounts.all})` },
                      ...(txCatCounts.__pending__ > 0
                        ? [{ key: "__pending__", label: `K zařazení (${txCatCounts.__pending__})` }]
                        : []),
                      ...L1_ORDER.filter((l1) => txCatCounts[l1] > 0).map((l1) => ({
                        key: l1,
                        label: `${l1} (${txCatCounts[l1]})`,
                      })),
                    ]}
                  />
                </div>

                {/* Hromadný výběr. Stejný pruh jako u notifikací: tichý řádek přes
                    celou šířku karty, akce se ukážou až když je něco zaškrtnuté. */}
                {visibleTransactions.length > 0 && !bulkEditing && (
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 sm:px-4">
                    <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs font-semibold text-slate-600">
                      <input
                        type="checkbox"
                        checked={vseTxVybrano}
                        onChange={prepniVsechnyTx}
                        className="h-4 w-4 accent-brand"
                      />
                      {selectedTxIds.size > 0 ? `${selectedTxIds.size} vybráno` : "Vybrat vše"}
                    </label>
                    {selectedTxIds.size > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button onClick={() => setBulkEditing(true)} className="btn btn-primary">
                          <Sparkles className="h-3.5 w-3.5" /> Zařadit ({selectedTxIds.size})
                        </button>
                        <button onClick={() => setSelectedTxIds(new Set())} className="btn btn-ghost">
                          Zrušit výběr
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Hromadný editor kategorie */}
                {bulkEditing && (
                  <div className="border-b border-slate-200 bg-brand-light/60 p-3 sm:p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-bold text-brand-950">
                      <Sparkles className="h-4 w-4" />
                      Zařadit {selectedTxIds.size} transakcí, vyber kategorii pro všechny vybrané
                    </div>
                    <BulkCategoryPicker
                      categories={categories}
                      onPick={(l1, l2, l3) => handleBulkClassify(l1, l2, l3)}
                      onCancel={() => setBulkEditing(false)}
                    />
                  </div>
                )}

                <div className="p-3 sm:p-4">
                {visibleTransactions.length === 0 ? (
                  <div className="text-center py-8 text-sm text-slate-400">
                    {transactions.length === 0
                      ? "Pro tento měsíc zatím nejsou žádné transakce. Platby normálně přitečou samy z e-mailu, případně můžeš na počítači nahrát PDF výpis v kartě Import z banky."
                      : "Žádné transakce odpovídající filtru."}
                  </div>
                ) : (
                  <TransactionTable
                    transactions={visibleTransactions}
                    categories={categories}
                    onClassify={handleClassify}
                    fapiInvoices={fapiInvoices}
                    editingTxId={editingTxId}
                    setEditingTxId={setEditingTxId}
                    selectedTxIds={selectedTxIds}
                    onToggleSelected={toggleTxSelected}
                  />
                )}
                </div>
              </KartaPrehledu>
            </div>

            {/* P&L breakdown — collapsible tree */}
            <div className="space-y-4 lg:col-span-2">
              {/* 13.9.2026 (kousek 6/7, rozhodnutí Č1): rozpad je jedna karta ze
                  vzorníku. Dřív měla každá z osmi skupin vlastní barvu, zelenou,
                  růžovou, modrou, fialovou, švestkovou, žlutou a dvě šedé, a nebylo
                  poznat, která položka je velká. Teď barví jen znaménko a velikost
                  ukazuje proužek podílu proti největší skupině. */}
              <KartaPrehledu>
                <HlavickaDlazdice
                  ikona={BarChart3}
                  nazev="Rozpad P&L"
                  odznak={monthLabel(monthKey)}
                  napoveda="Klepni na skupinu, rozbalí se podkategorie, klepnutím na podkategorii se ukážou samotné transakce. Transakci můžeš rovnou přeřadit, když sedí ve špatné kategorii. Proužek ukazuje, jak je skupina velká proti té největší, pilulka je počet plateb."
                />
                {plTree.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-400">Žádná zařazená data</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {plTree.map((group) => (
                      <PnLGroup
                        key={group.l1}
                        group={group}
                        maxTotal={plMaxTotal}
                        categories={categories}
                        onClassify={handleClassify}
                        fapiInvoices={fapiInvoices}
                        editingTxId={editingTxId}
                        setEditingTxId={setEditingTxId}
                      />
                    ))}
                  </div>
                )}
              </KartaPrehledu>

              {/* Aktivní pravidla — s bulk delete */}
              <RulesCard
                rules={rules}
                selectedRuleIds={selectedRuleIds}
                toggleRuleSelected={toggleRuleSelected}
                onBulkDelete={handleBulkDeleteRules}
                onSingleDelete={handleDeleteRule}
                onSelectAll={() => setSelectedRuleIds(new Set(rules.filter(r => !r.zruseno_kdy).map(r => r.id)))}
                onClearSelection={() => setSelectedRuleIds(new Set())}
              />
            </div>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

// Picker měsíce — omezený na MIN_MONTH ≤ x ≤ aktuální měsíc (do budoucna nelze)
function MonthPicker({ monthKey, setMonthKey }) {
  const [y, m] = monthKey.split("-");
  const todayMk = currentMonthKey();

  // Předchozí měsíc
  let pmm = parseInt(m, 10) - 1;
  let pyy = parseInt(y, 10);
  if (pmm === 0) { pmm = 12; pyy--; }
  const prevKey = `${pyy}-${String(pmm).padStart(2, "0")}`;
  const hasPrev = prevKey >= MIN_MONTH;

  // Následující měsíc — povolíme jen pokud nebude v budoucnu
  let nmm = parseInt(m, 10) + 1;
  let nyy = parseInt(y, 10);
  if (nmm === 13) { nmm = 1; nyy++; }
  const nextKey = `${nyy}-${String(nmm).padStart(2, "0")}`;
  const hasNext = nextKey <= todayMk;

  const goPrev = () => { if (hasPrev) setMonthKey(prevKey); };
  const goNext = () => { if (hasNext) setMonthKey(nextKey); };

  return (
    <ListovaniMesice
      popis={monthLabel(monthKey)}
      onZpet={goPrev}
      onVpred={goNext}
      zpetVypnuto={!hasPrev}
      vpredVypnuto={!hasNext}
      naDnesek={monthKey !== todayMk ? () => setMonthKey(todayMk) : undefined}
      popisDneska="Na tento měsíc"
    />
  );
}

// === TRANSACTION LIST — card-style rows pro hezčí vzhled ===
function TransactionTable({ transactions, categories, onClassify, fapiInvoices, editingTxId, setEditingTxId, selectedTxIds, onToggleSelected }) {
  return (
    <div className="space-y-1.5">
      {transactions.map((t) => (
        <TransactionRow
          key={t.id}
          tx={t}
          categories={categories}
          onClassify={onClassify}
          fapiInvoices={fapiInvoices}
          editing={editingTxId === t.id}
          onToggleEdit={() => setEditingTxId(editingTxId === t.id ? null : t.id)}
          onCloseEdit={() => setEditingTxId(null)}
          selected={selectedTxIds?.has(t.id)}
          onToggleSelected={onToggleSelected ? () => onToggleSelected(t.id) : null}
        />
      ))}
    </div>
  );
}

function TransactionRow({ tx, categories, onClassify, fapiInvoices, editing, onToggleEdit, onCloseEdit, selected, onToggleSelected }) {
  const uzka = useUzkaObrazovka();
  // 30.9.2026 (hlášení #129): řádek se nekreslí jen na celou šířku stránky, ale i
  // v úzkém panelu Rozpad P&L. Tam se počítačová podoba (datum, popis, částka,
  // kategorie a šipka vedle sebe) nevejde a texty se přetisknou přes sebe.
  // Rozhoduje proto šířka samotného řádku, ne jen šířka obrazovky.
  const radekRef = useRef(null);
  const [uzkyRadek, setUzkyRadek] = useState(false);
  useEffect(() => {
    const el = radekRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((zaznamy) => {
      for (const z of zaznamy) setUzkyRadek(z.contentRect.width < 560);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const kompakt = uzka || uzkyRadek;
  const isPending = !tx.kategorie_l1;
  const amount = parseFloat(String(tx.castka_czk).replace(/[ \u00a0]/g, "").replace(",", ".")) || 0;
  const isIncome = tx.typ === "kredit";

  const description = tx.merchant
    || tx.protiucet_nazev
    || tx.protiucet_cislo
    || (tx.poznamka || "").substring(0, 60)
    || "—";

  // Krátké zobrazení datumu jako "11. Bře" (lépe vizuálně než ISO)
  const dateShort = (() => {
    const m = String(tx.datum || "").match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return tx.datum;
    return `${parseInt(m[3], 10)}. ${MONTH_NAMES[parseInt(m[2], 10) - 1]}`;
  })();

  // Barva podle stavu
  const stateClass = editing
    ? "bg-emerald-50 border-emerald-300 ring-2 ring-emerald-400"
    : selected
      ? "bg-emerald-50/70 border-emerald-300"
      : isPending
        ? "bg-amber-50/40 border-amber-200 hover:bg-amber-50 hover:border-amber-300"
        : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300";

  return (
    <>
      <div
        ref={radekRef}
        className={`rounded-xl border ${stateClass} transition-all px-3 py-2.5 flex items-center gap-3`}
      >
        {/* Checkbox pro multi-select */}
        {onToggleSelected && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={(e) => { e.stopPropagation(); onToggleSelected(); }}
            onClick={(e) => e.stopPropagation()}
            className="h-5 w-5 shrink-0 cursor-pointer accent-brand"
            title={selected ? "Odebrat z výběru" : "Přidat do výběru"}
          />
        )}

        {/* Klikací oblast pro otevření editoru. Na telefonu je z jednoho řádku
            dvouřádková karta: datum a částka se dřív přetiskly přes sebe a
            odznak „Klasifikovat" vyjel mimo obrazovku (10.9.2026). */}
        <button onClick={onToggleEdit} className={`min-w-0 flex-1 text-left ${kompakt ? "flex flex-col gap-2" : "flex items-center gap-3"}`}>
        {kompakt ? (
          <>
            <span className="flex w-full min-w-0 items-center gap-2.5">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${isIncome ? "bg-emerald-100" : "bg-red-100"}`}>
                {isIncome ? <TrendingUp className="h-4 w-4 text-emerald-700" /> : <TrendingDown className="h-4 w-4 text-red-700" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 demo-sensitive" title={description}>{description}</span>
              <span className={`shrink-0 whitespace-nowrap text-[15px] font-bold tabular-nums ${isIncome ? "text-emerald-700" : "text-red-700"}`}>
                {isIncome ? "+" : "−"}{fmt(Math.abs(amount))} Kč
              </span>
            </span>
            <span className="flex w-full min-w-0 flex-wrap items-center gap-1.5">
              <Pilulka>{dateShort}</Pilulka>
              {tx.vs && <Pilulka><span className="demo-sensitive">VS {tx.vs}</span></Pilulka>}
              {isPending ? (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800">
                  <AlertCircle className="h-3 w-3" /> Zařadit
                </span>
              ) : (
                <span className="ml-auto min-w-0 max-w-[60%]">
                  <Pilulka><span className="truncate">{tx.kategorie_l3 || tx.kategorie_l2}</span></Pilulka>
                </span>
              )}
            </span>
          </>
        ) : (
          <>
        {/* Ikona kredit/debet */}
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isIncome ? "bg-emerald-100" : "bg-red-100"}`}>
          {isIncome ? <TrendingUp className="w-4 h-4 text-emerald-700" /> : <TrendingDown className="w-4 h-4 text-red-700" />}
        </div>

        {/* Datum + popis */}
        <div className="flex-1 min-w-0">
          <div className="flex min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap text-xs text-slate-500">
            <span className="shrink-0 font-mono">{dateShort}</span>
            {tx.typ_transakce && tx.typ_transakce !== "?" && (
              <span className="truncate">{tx.typ_transakce}</span>
            )}
            {tx.vs && <span className="shrink-0 demo-sensitive">VS {tx.vs}</span>}
          </div>
          <div className="text-sm font-semibold text-slate-800 truncate demo-sensitive" title={description}>{description}</div>
        </div>

        {/* Částka */}
        <div className="shrink-0 whitespace-nowrap text-right">
          <div className={`text-base font-bold tabular-nums ${isIncome ? "text-emerald-700" : "text-red-700"}`}>
            {isIncome ? "+" : "−"}{fmt(Math.abs(amount))} Kč
          </div>
        </div>

        {/* Status / kategorie badge */}
        <div className="w-32 shrink-0 text-right xl:w-44">
          {isPending ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800">
              <AlertCircle className="h-3 w-3" /> Zařadit
            </span>
          ) : (
            <div className="text-right">
              <div className="text-sm font-semibold text-slate-700 truncate">{tx.kategorie_l3 || tx.kategorie_l2}</div>
              <div className="text-[11px] text-slate-500 truncate">{tx.kategorie_l1}</div>
            </div>
          )}
        </div>

        {/* Chevron — vizuální indikátor expand */}
        <div className="shrink-0">
          {editing
            ? <ChevronDown className="w-4 h-4 text-emerald-600" />
            : <ChevronRight className="w-4 h-4 text-slate-300" />}
        </div>
          </>
        )}
        </button>
      </div>

      {editing && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-3 -mt-0.5">
          <CategoryEditor
            tx={tx}
            categories={categories}
            fapiInvoices={fapiInvoices}
            onSave={(l1, l2, l3, rule) => {
              onClassify(tx.id, l1, l2, l3, rule);
              onCloseEdit();
            }}
            onCancel={onCloseEdit}
          />
        </div>
      )}
    </>
  );
}


// Smart normalizace merchant názvu — odstraní unikátní transakční ID, aby pravidlo
// matchlo VŠECHNY platby od stejného merchanta (Meta/FB, BOLT, SLACK, atd.)
// Příklady:
//   "FACEBK *9TAA8JD8M2"    → "FACEBK"
//   "FACEBK *AHGXMHR8M2"    → "FACEBK"
//   "BOLT.EU/B/2603020323"  → "BOLT.EU"
//   "SLACK T05KRAVC5D4"     → "SLACK"
//   "GOPAY *MIOWEB.CZ"      → "GOPAY *MIOWEB.CZ" (zachovat — MIOWEB.CZ je doména, ne ID)
//   "GOOGLE WORKSPACE_zateckal" → "GOOGLE WORKSPACE"
function normalizeMerchantBase(m) {
  if (!m) return m;
  let s = String(m).trim();
  // 1. " *XXXXXXXXXX" — hvězdička + alfanumerický kód 6+ znaků BEZ tečky (čistý ID)
  s = s.replace(/\s*\*[A-Z0-9]{6,}(?![.\w])$/, "").trim();
  // 2. "/B/12345678" — slash + písmeno + slash + číslice (BOLT konvence)
  s = s.replace(/\/[A-Z]\/\d{4,}$/, "").trim();
  // 3. " ABCDEFGHIJ" — mezera + identifier alfanumerický 8+ chars (SLACK konvence)
  s = s.replace(/\s+[A-Z][A-Z0-9]{7,}$/, "").trim();
  // 4. "_zateckal" — underscore + slovo (Google Workspace_zateckal varianta)
  s = s.replace(/_[a-zA-Z][\w]+$/, "").trim();
  return s || String(m).trim();
}

// Mapuje název služby z FAPI faktury na nejvíc pravděpodobnou Revenue kategorii
function mapFapiServiceToCategory(serviceName) {
  if (!serviceName) return null;
  const s = serviceName.toLowerCase();
  if (/permanent|trénink|training|balíček|gold|premium|flexible|basic|zkušebn/i.test(s)) {
    return { l1: "Revenue", l2: "Tréninky", l3: "" };
  }
  if (/výživ|nutri|jídelníč|jidelnic|stravov|nutrition/i.test(s)) {
    return { l1: "Revenue", l2: "Výživa", l3: "" };
  }
  if (/b2b|firma|firemn|korporát|den zdraví|workshop/i.test(s)) {
    return { l1: "Revenue", l2: "B2B", l3: "" };
  }
  return { l1: "Revenue", l2: "Ostatní", l3: "" };
}

function CategoryEditor({ tx, categories, onSave, onCancel, fapiInvoices = [] }) {
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");

  // === FAPI lookup CLIENT-SIDE z pre-loaded faktur — instantní, žádné backend čekání ===
  const fapiHint = useMemo(() => {
    if (tx.typ !== "kredit") return null;
    if (!tx.vs && !tx.protiucet_nazev) return null;
    if (!fapiInvoices?.length) return null;

    // Fuzzy tokenizer — lowercase + strip diacritics + split na slova
    // Řeší rozdíl "TUMOVA JANA" (banka) vs "Jana Tůmová" (FAPI):
    // oba tokenizují na {"jana", "tumova"} → match
    const tokenize = (s) => String(s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .split(/[\s,.\-]+/)
      .filter((w) => w.length >= 2);

    const nameMatch = (bankName, invoiceName) => {
      const b = tokenize(bankName);
      const i = tokenize(invoiceName);
      if (!b.length || !i.length) return false;
      // Všechna slova z kratší strany musí být v delší
      const short = b.length <= i.length ? b : i;
      const long = new Set(b.length > i.length ? b : i);
      return short.every((w) => long.has(w));
    };

    let match = null;
    if (tx.vs) {
      match = fapiInvoices.find((inv) => String(inv.vs) === String(tx.vs));
    }
    if (!match && tx.protiucet_nazev) {
      const matches = fapiInvoices.filter((inv) => nameMatch(tx.protiucet_nazev, inv.customer));
      if (matches.length === 1) match = matches[0];
      else if (matches.length > 1 && tx.datum) {
        const t0 = new Date(tx.datum).getTime();
        matches.sort((a, b) => Math.abs(new Date(a.createDate || 0).getTime() - t0) - Math.abs(new Date(b.createDate || 0).getTime() - t0));
        match = matches[0];
      } else if (matches.length >= 1) {
        match = matches[0];
      }
    }
    if (!match) return null;
    return {
      invoiceNumber: match.number,
      customer: match.customer,
      serviceName: match.serviceName || "",
      total: match.total,
      createDate: match.createDate,
      variableSymbol: match.vs,
      proforma: match.proforma,
    };
  }, [tx, fapiInvoices]);

  const fapiCategory = useMemo(() => fapiHint ? mapFapiServiceToCategory(fapiHint.serviceName) : null, [fapiHint]);

  // Auto-pravidlo se vytváří vždy z nejlepšího dostupného atributu.
  // User může jedním klikem přepnout na jiný atribut (např. castka_czk
  // pro „všechny 490 Kč = zkušební trénink") nebo pravidlo úplně vypnout.
  // Pokud je k dispozici JEN částka (žádný unikátní identifikátor) → default OFF
  // protože pravidlo „250 Kč → kategorie" by chytlo úplně cokoliv.
  const hasStrongIdentifier = !!(tx.merchant || tx.protiucet_nazev || tx.vs || tx.protiucet_cislo);
  const [createRule, setCreateRule] = useState(hasStrongIdentifier);
  // Direction — defaultně podle typu transakce (kredit/debet), user může přepnout na "any"
  const [direction, setDirection] = useState(tx.typ === "kredit" ? "kredit" : "debet");
  const patternOptions = useMemo(() => {
    const opts = [];
    // FAPI služba má nejvyšší prioritu — nejvíce specifická pro Revenue tx
    if (fapiHint && fapiHint.serviceName) {
      opts.push({ typ: "fapi_service", val: fapiHint.serviceName, label: "Stejnou službu z FAPI", icon: FileText });
    }
    if (tx.merchant) {
      const base = normalizeMerchantBase(tx.merchant);
      // Pokud normalizace skutečně něco zkrátila → nabídneme OBĚ varianty
      if (base && base !== tx.merchant) {
        opts.push({ typ: "merchant", val: base, label: "Stejná značka", note: `vč. všech kódů (např. ${tx.merchant})` });
        opts.push({ typ: "merchant", val: tx.merchant, label: "Přesně tento kód", note: "jen tato konkrétní platba" });
      } else {
        opts.push({ typ: "merchant", val: tx.merchant, label: "Stejný merchant" });
      }
    }
    if (tx.protiucet_nazev) opts.push({ typ: "protiucet_nazev", val: tx.protiucet_nazev, label: "Stejné jméno" });
    // Priorita: číslo účtu PŘED VS (VS se u stejného dodavatele často mění, číslo účtu je stabilnější)
    if (tx.protiucet_cislo) opts.push({ typ: "protiucet_cislo", val: tx.protiucet_cislo, label: "Stejné číslo účtu" });
    if (tx.vs) opts.push({ typ: "vs", val: String(tx.vs), label: "Stejný VS" });
    const amt = Math.abs(parseFloat(String(tx.castka_czk || "0").replace(/[ \u00a0]/g, "").replace(",", ".")));
    if (amt > 0) opts.push({ typ: "castka_czk", val: String(amt), label: "Stejnou částku", suffix: " Kč" });
    return opts;
  }, [tx, fapiHint]);
  // Default — první dostupný atribut (FAPI > merchant > jméno > VS)
  const [pickedPatternIdx, setPickedPatternIdx] = useState(0);
  const autoPattern = patternOptions[pickedPatternIdx] || null;

  // Plochý seznam všech kategorií (filtrovaný + setříděný podle L1 pořadí + dedup)
  const flatList = useMemo(() => {
    const q = search.trim().toLowerCase();
    const seen = new Set();
    const out = [];
    for (const c of categories) {
      const l1 = c.kategorie_l1 || "Ostatní";
      const l2 = c.kategorie_l2 || "";
      const l3 = c.kategorie_l3 || "";
      // Dedup — stejná kombinace L1/L2/L3 by se neměla opakovat (ochrana proti duplicitám v datech)
      const key = `${l1}|${l2}|${l3}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (q) {
        const hay = `${l1} ${l2} ${l3}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      out.push({ l1, l2, l3, label: l3 || l2, path: `${l2}${l3 ? " / " + l3 : ""}` });
    }
    out.sort((a, b) => {
      const ia = L1_ORDER.indexOf(a.l1), ib = L1_ORDER.indexOf(b.l1);
      const sa = ia === -1 ? 999 : ia;
      const sb = ib === -1 ? 999 : ib;
      if (sa !== sb) return sa - sb;
      return (a.l2 + a.l3).localeCompare(b.l2 + b.l3);
    });
    return out;
  }, [categories, search]);

  // Chytré návrhy: heuristický scoring podle dostupných atributů transakce
  const suggestions = useMemo(() => {
    if (search.trim()) return []; // při hledání nepleť
    const m = (tx.merchant || "").toLowerCase();
    const proti = (tx.protiucet_nazev || "").toLowerCase();
    const isIncome = tx.typ === "kredit";
    const amt = Math.abs(parseFloat(String(tx.castka_czk || "0").replace(/[ \u00a0]/g, "").replace(",", ".")));

    // Slovník merchant → kategorie keyword
    const hints = [
      { match: ["facebk", "facebook", "fb.me"], cat: ["Marketing & Sales", "Ads", "Facebook"] },
      { match: ["google ads", "googleads", "google.ads"], cat: ["Marketing & Sales", "Ads", "Google"] },
      { match: ["seznam.cz", "sklik"], cat: ["Marketing & Sales", "Ads", "Seznam"] },
      { match: ["google workspace", "google services"], cat: ["General & Administration", "Apps", ""] },
      { match: ["slack"], cat: ["General & Administration", "Apps", ""] },
      { match: ["notion"], cat: ["General & Administration", "Apps", ""] },
      { match: ["canva", "figma"], cat: ["General & Administration", "Apps", ""] },
      { match: ["bolt", "uber"], cat: ["Others & Backup", "Travel costs & car", "Bolt / Uber"] },
      { match: ["benzina", "shell", "mol", "orlen", "okd"], cat: ["Others & Backup", "Travel costs & car", "Pohonné hmoty"] },
      { match: ["parkovne", "parkov"], cat: ["Others & Backup", "Travel costs & car", "Parkovné"] },
      { match: ["fapi"], cat: ["General & Administration", "Apps", ""] },
      { match: ["miokweb", "miowem", "gopay"], cat: ["General & Administration", "Apps", ""] },
      { match: ["reservio"], cat: ["General & Administration", "Apps", ""] },
    ];

    const found = new Set();
    const results = [];
    for (const h of hints) {
      if (h.match.some(k => m.includes(k) || proti.includes(k))) {
        const key = h.cat.join("|");
        if (!found.has(key) && categories.some(c => c.kategorie_l1 === h.cat[0] && c.kategorie_l2 === h.cat[1] && (c.kategorie_l3 || "") === h.cat[2])) {
          found.add(key);
          results.push({ l1: h.cat[0], l2: h.cat[1], l3: h.cat[2], reason: `merchant: ${h.match[0]}` });
        }
      }
    }

    // Příchozí platba s typickou „tréninkovou" částkou → návrh Revenue/Tréninky
    if (isIncome && categories.some(c => c.kategorie_l1 === "Revenue" && c.kategorie_l2 === "Tréninky")) {
      if ([490, 990, 1490, 1990, 2490, 2990, 3490, 3990, 4490, 4990, 7350, 14700].includes(amt)) {
        results.push({ l1: "Revenue", l2: "Tréninky", l3: "", reason: `částka ${amt} Kč (typický balíček)` });
      }
    }
    // Příchozí platba bez merchantu od fyzické osoby (jméno v protiucet_nazev) → Revenue/Tréninky default
    if (isIncome && proti && !m && categories.some(c => c.kategorie_l1 === "Revenue" && c.kategorie_l2 === "Tréninky") && !results.length) {
      results.push({ l1: "Revenue", l2: "Tréninky", l3: "", reason: `příchozí od ${tx.protiucet_nazev}` });
    }

    return results.slice(0, 4);
  }, [tx, categories, search]);

  const handlePick = (l1, l2, l3) => {
    const rule = createRule && autoPattern
      ? { pattern_typ: autoPattern.typ, pattern_hodnota: autoPattern.val, direction, poznamka: note || undefined }
      : null;
    onSave(l1, l2, l3, rule);
  };

  const isIncome = tx.typ === "kredit";
  const amount = parseFloat(String(tx.castka_czk).replace(/[ \u00a0]/g, "").replace(",", ".")) || 0;
  const subtitle = tx.merchant || tx.protiucet_nazev || tx.protiucet_cislo || "";

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header — info o transakci */}
      <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isIncome ? "bg-emerald-100" : "bg-red-100"}`}>
              {isIncome ? <TrendingUp className="w-6 h-6 text-emerald-700" /> : <TrendingDown className="w-6 h-6 text-red-700" />}
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">{formatDate(tx.datum)}, {tx.typ_transakce || tx.typ}</div>
              <div className={`text-2xl font-bold tabular-nums ${isIncome ? "text-emerald-700" : "text-red-700"}`}>
                {isIncome ? "+" : "−"}{fmt(Math.abs(amount))} Kč
              </div>
              {subtitle && <div className="text-sm text-slate-700 mt-0.5">{subtitle}</div>}
              {tx.vs && <div className="text-xs text-slate-400">VS: {tx.vs}{tx.ks ? `, KS: ${tx.ks}` : ""}{tx.castka_orig ? `, originál: ${tx.castka_orig} ${tx.mena_orig || ""}` : ""}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNote((s) => !s)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xl border transition-colors ${showNote ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}
              title="Přidat poznámku k pravidlu"
            >
              <FileText className="w-3.5 h-3.5" />
              {showNote ? "Skrýt poznámku" : "Přidat poznámku"}
            </button>
            <button onClick={onCancel} className="p-1.5 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 min-h-[44px] sm:min-h-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Volitelná poznámka — jen když user klikne */}
        {showNote && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <label className="text-xs text-slate-600 font-medium mb-1 block">Poznámka k pravidlu (volitelné)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Např. Měsíční předplatné Slack pro tým"
              className="field w-full"
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Search box */}
      <div className="px-4 pt-3 pb-2 border-b border-slate-200 bg-slate-50/40">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Hledat kategorii…"
          className="field w-full"
        />
      </div>

      {/* Pattern selector — z čeho se vytvoří auto-pravidlo */}
      {patternOptions.length > 0 && (
        <div className="px-4 py-2.5 bg-emerald-50/50 border-b border-emerald-100">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-slate-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
              <span className="font-medium">Pravidlo bude reagovat na:</span>
              <span className={`text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${createRule ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                {createRule ? "✓ ZAPNUTO" : "⊘ VYPNUTO"}
              </span>
            </div>
            <button
              onClick={() => setCreateRule((s) => !s)}
              className={`text-xs px-2.5 py-1 rounded font-semibold border ${createRule ? "bg-white text-slate-700 border-slate-300 hover:bg-slate-50" : "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"}`}
            >
              {createRule ? "Vypnout pravidlo" : "Zapnout pravidlo"}
            </button>
          </div>
          {createRule && !hasStrongIdentifier && autoPattern?.typ === "castka_czk" && (
            <div className="mt-2 px-2 py-1.5 rounded-lg bg-amber-100 border border-amber-300 text-xs text-amber-900">
              Pravidlo by reagovalo jen na částku, nemá unikátní identifikátor (merchant/jméno/VS), takže by chytlo všechny tx s touto částkou. Zvaž zda opravdu chceš.
            </div>
          )}
          {createRule && (
            <>
              {/* Direction selector — kdy se má pravidlo aplikovat */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-wider text-slate-600 font-bold">aplikovat na:</span>
                <button
                  onClick={() => setDirection("debet")}
                  className={`text-xs px-2 py-1 rounded border ${direction === "debet" ? "bg-red-600 text-white border-red-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                  title="Pravidlo se aplikuje jen na výdaje"
                >Jen výdaje</button>
                <button
                  onClick={() => setDirection("kredit")}
                  className={`text-xs px-2 py-1 rounded border ${direction === "kredit" ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                  title="Pravidlo se aplikuje jen na příjmy"
                >Jen příjmy</button>
                <button
                  onClick={() => setDirection("any")}
                  className={`text-xs px-2 py-1 rounded border ${direction === "any" ? "bg-slate-700 text-white border-slate-800" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                  title="Pravidlo se aplikuje na příjmy i výdaje"
                >Obojí</button>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-2">
                {patternOptions.map((opt, i) => {
                const active = i === pickedPatternIdx;
                const isFapi = opt.typ === "fapi_service";
                const baseColor = isFapi
                  ? (active ? "bg-plum-600 text-white border-plum-700 shadow-sm" : "bg-plum-50 text-plum-800 border-plum-300 hover:bg-plum-100")
                  : (active ? "bg-emerald-600 text-white border-emerald-700 shadow-sm" : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400 hover:bg-emerald-50");
                const Icon = opt.icon;
                return (
                  <button
                    key={i}
                    onClick={() => setPickedPatternIdx(i)}
                    className={`text-sm px-3 py-2 rounded-xl border-2 transition-all text-left ${baseColor}`}
                  >
                    <div className={`text-[11px] uppercase tracking-wider font-bold flex items-center gap-1 ${active ? "opacity-90" : isFapi ? "" : "text-slate-500"}`}>
                      {Icon && <Icon className="w-3 h-3" />}
                      {opt.label}
                    </div>
                    <div className="font-mono font-semibold text-sm truncate max-w-[260px]" title={opt.val}>{opt.val}{opt.suffix || ""}</div>
                    {opt.note && <div className={`text-[11px] mt-0.5 ${active ? "opacity-80" : "text-slate-500 italic"}`}>{opt.note}</div>}
                  </button>
                );
              })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Chytré návrhy + scrollovatelný seznam kategorií */}
      <div className="p-3 space-y-3">
        {/* === FAPI HINT — info o faktuře (instantně z pre-loaded cache) === */}
        {fapiHint && (
          <div className="bg-gradient-to-br from-plum-50 to-brand-50 border-2 border-plum-300 rounded-xl p-3.5 shadow-sm">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs uppercase tracking-wider text-plum-800 font-bold flex items-center gap-1.5">
                <FileText className="w-4 h-4" /> Faktura z FAPI
                <InfoTip text={`Našel jsem ve FAPI fakturu odpovídající VS / jménu klienta. Pomocí chipu „Stejnou službu z FAPI" v sekci níže můžeš vytvořit pravidlo, které automaticky klasifikuje všechny budoucí faktury se stejnou službou stejnou kategorií.`} />
              </div>
              <span className="text-[11px] text-slate-500">{fapiHint.invoiceNumber}{fapiHint.proforma ? ", zálohová" : ""}</span>
            </div>
            <div className="space-y-1.5">
              <div className="text-lg font-bold text-slate-800">
                {fapiHint.serviceName || "(bez názvu položky)"}
              </div>
              <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
                <span><b>{fapiHint.customer}</b></span>
                {fapiHint.variableSymbol && <span>VS {fapiHint.variableSymbol}</span>}
                {fapiHint.total > 0 && <span>faktura {fmt(fapiHint.total)} Kč</span>}
                {fapiHint.createDate && <span>vystaveno {formatDate(String(fapiHint.createDate).substring(0, 10))}</span>}
              </div>
              <div className="text-xs text-plum-700 italic mt-1">
                Tip: vyber kategorii níže a klik na chip „Stejnou službu z FAPI" v sekci „Pravidlo bude reagovat na", pravidlo pak automaticky zařadí všechny pending faktury se stejnou službou.
              </div>
            </div>
          </div>
        )}

        {/* Chytré návrhy — jen když jich pár máme a user nehledá */}
        {suggestions.length > 0 && (
          <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-2.5">
            <div className="text-xs uppercase tracking-wider text-emerald-800 font-bold mb-1.5 flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> Doporučené (klik = uložit)
            </div>
            <div className="space-y-1">
              {suggestions.map((s, i) => {
                const c = L1_COLOR[s.l1] || L1_COLOR[""];
                return (
                  <button
                    key={i}
                    onClick={() => handlePick(s.l1, s.l2, s.l3)}
                    className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-xl border-2 ${c.border} ${c.bg} hover:shadow-md hover:brightness-95 transition-all`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-2 h-2 rounded-full ${c.solid} shrink-0`} />
                      <span className={`font-bold text-sm ${c.text}`}>{s.l1}</span>
                      <span className="text-sm text-slate-700 truncate">{s.l2}{s.l3 ? ` / ${s.l3}` : ""}</span>
                    </div>
                    <span className="text-[11px] text-slate-500 italic shrink-0">{s.reason}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Hlavní seznam — single column, scrollable */}
        {flatList.length === 0 ? (
          <div className="text-sm text-slate-500 italic p-6 text-center bg-slate-50 rounded-xl border border-slate-200">
            {search ? `Nic pro „${search}". Zkus jiný výraz.` : "Seznam kategorií je prázdný."}
          </div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <div className="max-h-[420px] overflow-y-auto">
              {(() => {
                // Render seznam s L1 mini-hlavičkami (sticky)
                const out = [];
                let lastL1 = null;
                for (let i = 0; i < flatList.length; i++) {
                  const item = flatList[i];
                  const c = L1_COLOR[item.l1] || L1_COLOR[""];
                  if (item.l1 !== lastL1) {
                    out.push(
                      <div key={`hdr-${item.l1}`} className={`sticky top-0 z-10 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold ${c.solid} text-white border-y border-white/20`}>
                        {item.l1}
                      </div>
                    );
                    lastL1 = item.l1;
                  }
                  out.push(
                    <button
                      key={`row-${i}`}
                      onClick={() => handlePick(item.l1, item.l2, item.l3)}
                      className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-1.5 h-1.5 rounded-full ${c.solid} shrink-0`} />
                        <span className="text-sm text-slate-700 truncate">
                          <span className="text-slate-500">{item.l2}</span>
                          {item.l3 && (
                            <>
                              <span className="text-slate-400 mx-1">/</span>
                              <span className="font-medium text-slate-800">{item.l3}</span>
                            </>
                          )}
                        </span>
                      </div>
                      <span className="text-xs text-emerald-600 opacity-0 hover:opacity-100 shrink-0">→ vybrat</span>
                    </button>
                  );
                }
                return out;
              })()}
            </div>
            <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500">
              {flatList.length} {flatList.length === 1 ? "položka" : flatList.length < 5 ? "položky" : "položek"}{search ? ` pro „${search}"` : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// === ANOMALY STRIP — top alerty pro CEO ===
// 13.9.2026 (kousek 4/7, rozhodnutí Č1): anomálie jsou hlásky ze vzorníku,
// stejné jako varování jinde v aplikaci. Dřív to byly tři různě barevné boxy
// v mřížce, každý s vlastním odstínem.
function AnomalyStrip({ anomalies }) {
  if (!anomalies || anomalies.length === 0) return null;
  const tony = { danger: "problem", warning: "pozor", info: "pozor" };
  return (
    <div className="space-y-2">
      {anomalies.map((a, i) => (
        <Hlaska key={i} ton={tony[a.kind] || "pozor"} title={<span className="demo-sensitive">{a.text}</span>} />
      ))}
    </div>
  );
}

// 13.9.2026 (fáze 4f, rozhodnutí Č2): provozní výsledek je karta ve stejném
// tvaru jako ostatní, jen hlavička i pozadí drží barvu výsledku, zelenou
// v plusu a červenou v minusu. Dřív to byl velký barevný blok, jediný takový
// prvek v aplikaci. Trend za 6 měsíců je skupina uvnitř té samé karty.
function CeoHero({ topTotals, ceoMetrics, sparkData, pendingCount, txCount }) {
  const opPositive = topTotals.saldo >= 0;
  const t = opPositive
    ? { hlavicka: "from-emerald-800 to-emerald-600", telo: "bg-emerald-50", cislo: "text-emerald-800", popis: "text-emerald-700", pilulka: "bg-white text-emerald-800", pruh: "bg-emerald-500", stopa: "bg-emerald-100", ram: "border-emerald-200", cara: "border-emerald-100", Ikona: TrendingUp }
    : { hlavicka: "from-rose-800 to-rose-600", telo: "bg-rose-50", cislo: "text-rose-800", popis: "text-rose-700", pilulka: "bg-white text-rose-800", pruh: "bg-rose-500", stopa: "bg-rose-100", ram: "border-rose-200", cara: "border-rose-100", Ikona: TrendingDown };

  const zmena = (deltaPct) => {
    if (deltaPct === null || deltaPct === undefined) return null;
    const nahoru = deltaPct > 0;
    return `${nahoru ? "▲" : deltaPct < 0 ? "▼" : "•"} ${Math.abs(deltaPct)} % proti minulému měsíci`;
  };

  return (
    <div className="space-y-3">
      <div className={`overflow-hidden rounded-xl border ${t.ram} bg-white`}>
        <div className={`flex items-center gap-3 bg-gradient-to-r ${t.hlavicka} px-4 py-3 text-white sm:px-5`}>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15">
            <t.Ikona className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-serif text-lg font-bold leading-tight">Provozní výsledek</span>
            <span className="block text-xs text-white/80">příjmy minus výdaje za měsíc</span>
          </span>
          <InfoTip
            varianta="hlavicka"
            text="Provozní výsledek hospodaření za vybraný měsíc, tedy Revenue minus Expenses bez půjček, investic a interních převodů. Číslo, nad kterým CEO ráno přemýšlí."
          />
        </div>

        <div className={`${t.telo} px-4 py-5 text-center sm:px-5`}>
          <div className={`font-serif text-4xl font-bold tabular-nums sm:text-5xl ${t.cislo}`}>
            {opPositive ? "+" : "−"}{fmt(Math.abs(topTotals.saldo))}
          </div>
          <div className={`mt-1 text-sm font-semibold ${t.popis}`}>Kč</div>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
            {zmena(ceoMetrics.momOpResult) && (
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.pilulka}`}>{zmena(ceoMetrics.momOpResult)}</span>
            )}
            {ceoMetrics.eomOpResult !== null && ceoMetrics.eomOpResult !== undefined && (
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.pilulka}`}>
                odhad na konec {ceoMetrics.eomOpResult >= 0 ? "+" : "−"}{fmt(Math.abs(ceoMetrics.eomOpResult))} Kč
              </span>
            )}
            {ceoMetrics.isCurrentMonth && (
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.pilulka}`}>
                {ceoMetrics.daysElapsed} z {ceoMetrics.daysInMonth} dní
              </span>
            )}
          </div>
        </div>

        {ceoMetrics.isCurrentMonth && (
          <div className={`border-t ${t.cara} px-4 py-3 sm:px-5`}>
            <div className={`h-1.5 overflow-hidden rounded-full ${t.stopa}`}>
              <div className={`h-full rounded-full ${t.pruh}`} style={{ width: `${Math.round(ceoMetrics.monthProgress * 100)}%` }} />
            </div>
          </div>
        )}

        <SkupinaPrehledu
          ikona={TrendingUp}
          nazev="Trend posledních 6 měsíců"
          napoveda="Posledních šest měsíců provozního výsledku. Zelený sloupec je ziskový měsíc, růžový ztrátový. Aktuální měsíc má tečkovaný okraj a běží, hodnota se mění s každou novou transakcí."
        />
        <div className="p-3 sm:p-4">
          <SparklineTrend data={sparkData} />
        </div>
      </div>

      {/* Čtyři čísla pod výsledkem */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <HeroCard
          label="Příjmy"
          color="emerald"
          value={topTotals.revenue}
          deltaPct={ceoMetrics.momRevenue}
          deltaInvert={false}
          eomValue={ceoMetrics.eomRevenue}
          tip="Součet provozních příjmů, tedy Tréninky, Výživa, B2B a Ostatní. Půjčky, investice a interní převody jsou stranou. Změna proti minulému měsíci má být zelená."
        />
        <HeroCard
          label="Výdaje"
          color="red"
          value={Math.abs(topTotals.expenses)}
          deltaPct={ceoMetrics.momExpenses}
          deltaInvert
          eomValue={ceoMetrics.eomExpenses ? Math.abs(ceoMetrics.eomExpenses) : null}
          tip="Součet všech výdajových skupin: Marketing a Sales, G&A, Production, R&D, Others a Backup. Půjčky, investice a interní převody se nepočítají. Nárůst je červený, pokles zelený."
        />
        <HeroCard
          label="Tok peněz"
          color={topTotals.cashFlow >= 0 ? "emerald" : "red"}
          value={topTotals.cashFlow}
          subtitle={topTotals.neutralFlow !== 0
            ? `včetně ${topTotals.neutralFlow >= 0 ? "+" : "−"}${fmt(Math.abs(topTotals.neutralFlow))} Kč z půjček a převodů`
            : "bez půjček a převodů"}
          tip="Skutečný tok peněz na účet za měsíc: provozní výsledek plus půjčky a investice plus interní převody. Když je provoz ztrátový a tok peněz kladný, žiješ z půjčky nebo vlastního vkladu, a to je potřeba sledovat."
        />
        <HeroCard
          label="K vyřízení"
          color={pendingCount > 0 ? "amber" : "emerald"}
          value={pendingCount}
          unit=""
          subtitle={pendingCount === 0 ? "vše klasifikováno" : `z ${txCount} transakcí, klasifikuj je níž`}
          tip="Transakce bez kategorie. Bez nich je rozpad P&L nepřesný. Klepni na řádek v seznamu a vyber kategorii, uloží se hned a rovnou vznikne pravidlo pro příště."
        />
      </div>
    </div>
  );
}

// Dlaždice pod provozním výsledkem. Stejný tvar jako čísla na ostatních
// stránkách: barevný proužek nahoře, popisek, hodnota, pod ní pilulky.
function HeroCard({ label, color, value, deltaPct, deltaInvert, eomValue, subtitle, tip, unit = "Kč" }) {
  const pruhy = { emerald: "bg-emerald-500", red: "bg-rose-500", amber: "bg-amber-500" };
  const barvyHodnoty = { emerald: "text-emerald-700", red: "text-rose-700", amber: "text-amber-700" };

  let zmena = null;
  if (deltaPct !== null && deltaPct !== undefined) {
    const nahoru = deltaPct > 0;
    const dobre = deltaInvert ? !nahoru : nahoru;
    const barva = deltaPct === 0 ? "bg-slate-100 text-slate-600" : dobre ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700";
    zmena = (
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${barva}`}>
        {nahoru ? "▲" : deltaPct < 0 ? "▼" : "•"} {Math.abs(deltaPct)} %
      </span>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-glass">
      <div className={`h-1.5 ${pruhy[color] || pruhy.emerald}`} />
      <div className="p-3">
        <div className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
          <span className="min-w-0 truncate">{label}</span>
          {tip && <InfoTip text={tip} />}
        </div>
        <div className={`mt-0.5 text-xl font-bold tabular-nums sm:text-2xl ${barvyHodnoty[color] || barvyHodnoty.emerald}`}>
          {typeof value === "number" && value < 0 ? "−" : ""}{fmt(typeof value === "number" ? Math.abs(value) : value)}
          {unit && <span className="ml-1 text-sm font-semibold text-slate-500">{unit}</span>}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {zmena}
          {eomValue !== null && eomValue !== undefined && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
              odhad {fmt(eomValue)} Kč
            </span>
          )}
        </div>
        {subtitle && <div className="mt-1 text-xs leading-snug text-slate-500">{subtitle}</div>}
      </div>
    </div>
  );
}

// === SPARKLINE TREND — 6 měsíců provozního výsledku, custom CSS bary ===
// Místo recharts: lepší kontrola layoutu (osa uprostřed, hodnota u baru, čárkovaný okraj pro
// aktuální měsíc, gradient bary).
//
// 30.9.2026 (hlášení #128): graf původně seděl v barevném bloku, takže měl světlé písmo.
// Po přesunu do bílé karty byly popisky měsíců bílé na bílé a hodnoty skoro neviditelné.
// Barvy jsou teď tmavé na světlém podkladu a i nulový nebo drobný měsíc má vidět proužek.
function SparklineTrend({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map((d) => Math.abs(d.opResult || 0)), 1);
  const lastIdx = data.length - 1;
  return (
    <div>
      {/* Bar chart container — osa uprostřed (50/50 split) */}
      <div className="relative flex items-stretch justify-between gap-1.5 h-[120px]">
        {/* Centrální osa (přes celou šíři) */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-200 -translate-y-px pointer-events-none" />

        {data.map((d, i) => {
          const isCurrent = i === lastIdx;
          const value = d.opResult || 0;
          const positive = value >= 0;
          // max 90 % půlky (10 % rezerva pro hodnotu). Minimum 6 %, ať je i drobný
          // měsíc vidět jako proužek a nespadne na nulovou výšku.
          const heightPct = Math.max((Math.abs(value) / max) * 90, 6);
          const valueLabel = `${positive ? "+" : "−"}${fmtShort(Math.abs(value))}`;
          const barColor = positive
            ? (isCurrent
                ? "bg-emerald-100 border-2 border-dashed border-emerald-400"
                : "bg-gradient-to-t from-emerald-600 to-emerald-400")
            : (isCurrent
                ? "bg-rose-100 border-2 border-dashed border-rose-400"
                : "bg-gradient-to-b from-rose-600 to-rose-400");

          return (
            <div key={d.mk} className="flex-1 h-full flex flex-col items-center min-w-0 group">
              {/* Horní polovina */}
              <div className="flex-1 w-full flex flex-col justify-end items-center">
                {positive && (
                  <>
                    <div className={`text-[11px] font-bold leading-tight mb-0.5 demo-sensitive ${isCurrent ? "text-emerald-600" : "text-emerald-700"}`}>
                      {valueLabel}
                    </div>
                    <div
                      className={`w-full max-w-[32px] rounded-t-lg ${barColor} transition-opacity group-hover:opacity-90`}
                      style={{ height: `${heightPct}%` }}
                      title={`${d.label}: ${valueLabel} Kč${isCurrent ? " (běží)" : ""}`}
                    />
                  </>
                )}
              </div>
              {/* Dolní polovina */}
              <div className="flex-1 w-full flex flex-col justify-start items-center">
                {!positive && (
                  <>
                    <div
                      className={`w-full max-w-[32px] rounded-b-lg ${barColor} transition-opacity group-hover:opacity-90`}
                      style={{ height: `${heightPct}%` }}
                      title={`${d.label}: ${valueLabel} Kč${isCurrent ? " (běží)" : ""}`}
                    />
                    <div className={`text-[11px] font-bold leading-tight mt-0.5 demo-sensitive ${isCurrent ? "text-rose-600" : "text-rose-700"}`}>
                      {valueLabel}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Měsíční labely — pod chartem, aktuální zvýrazněný */}
      <div className="flex justify-between gap-1.5 mt-2">
        {data.map((d, i) => {
          const isCurrent = i === lastIdx;
          return (
            <div key={d.mk} className={`flex-1 text-center text-[11px] ${isCurrent ? "font-bold text-slate-900" : "text-slate-500"}`}>
              {d.label}
              {isCurrent && <span className="ml-0.5 opacity-80">⏳</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// === PŘEHLED ROKU (YTD + projekce na konec roku) ===
// Kumulativní bilance od ledna + lineární extrapolace na 31. 12. Doplňuje hlavní hero,
// kde je jen aktuální měsíc. Tady user vidí „kde celkově jsem v tomto roce a kam to jde".
// 13.9.2026 (fáze 4f, kousek 2/7): přehled roku je karta ze vzorníku. Dřív to
// byl šedomodrý rámeček s dvojitým ohraničením a v něm čtyři bílé boxy s dalším
// dvojitým ohraničením a proužkem vlevo. Teď je to tmavá hlavička, v ní pilulka
// s obdobím, pod ní pruh postupu roku a čtyři dlaždice. Na telefonu jde každá
// dlaždice přes celou šířku a částka sedí vpravo na řádku s popiskem, takže se
// ani sedmimístná suma nezlomí.
const MESICE_NAZEV = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];

function YearOverview({ yearTotals, monthKey }) {
  const yt = yearTotals;
  const progressPct = Math.round((yt.daysElapsed / yt.daysInYear) * 100);
  const mesicIndex = Math.max(0, Math.min(11, parseInt(String(monthKey || "").split("-")[1], 10) - 1));
  const obdobi = mesicIndex === 0 ? "leden" : `leden až ${MESICE_NAZEV[mesicIndex] || "prosinec"}`;

  const rows = [
    {
      label: "Příjmy",
      color: "emerald",
      ytd: yt.ytdRevenue,
      eoy: yt.eoyRevenue,
      avg: yt.avgMonthlyRevenue,
      tip: "Součet všech provozních příjmů od 1. ledna do konce zobrazeného měsíce. Odhad roku je lineární přepočet, tedy kam se dostaneš za současného tempa.",
    },
    {
      label: "Výdaje",
      color: "red",
      ytd: Math.abs(yt.ytdExpenses),
      eoy: Math.abs(yt.eoyExpenses),
      avg: Math.abs(yt.avgMonthlyExpenses),
      tip: "Součet všech provozních výdajů od 1. ledna: Marketing, G&A, Production, R&D a Others. Půjčky a interní převody se nezapočítávají.",
    },
    {
      label: "Provozní výsledek",
      color: yt.ytdOpResult >= 0 ? "emerald" : "red",
      ytd: yt.ytdOpResult,
      eoy: yt.eoyOpResult,
      avg: yt.avgMonthlyOpResult,
      signed: true,
      tip: "Kumulativní příjmy minus výdaje od ledna. Kladné číslo znamená, že si firma vydělává, záporné, že je zatím ve ztrátě.",
    },
    {
      label: "Tok peněz",
      color: yt.ytdCashFlow >= 0 ? "emerald" : "red",
      ytd: yt.ytdCashFlow,
      eoy: yt.eoyCashFlow,
      avg: yt.avgMonthlyCashFlow,
      signed: true,
      tip: "Skutečný pohyb peněz na účtech od ledna: provozní výsledek plus půjčky a investice, bez interních převodů mezi vlastními účty.",
    },
  ];

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Calendar}
        nazev={`Rok ${yt.currentYear}`}
        podtitulek="kumulativně od ledna"
        odznak={obdobi}
        napoveda={`Kumulativní součet od 1. ledna do konce zobrazeného měsíce. Odhad roku je lineární přepočet: hodnota od ledna dělená ${yt.daysElapsed} dny a vynásobená ${yt.daysInYear} dny. Průměr na měsíc slouží jako baseline, kolik firmě reálně přitéká a odtéká.`}
      />
      <div className="px-4 py-3 sm:px-5">
        <div className="mb-1 flex items-baseline justify-between gap-2 text-xs text-slate-500">
          <span>Rok proběhl</span>
          <span className="tabular-nums">
            {yt.daysElapsed} z {yt.daysInYear} dní ({progressPct} %)
          </span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-brand" style={{ width: `${progressPct}%` }} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-4">
        {rows.map((r) => (
          <YtdBox key={r.label} {...r} />
        ))}
      </div>
    </KartaPrehledu>
  );
}

function YtdBox({ label, color, ytd, eoy, avg, signed, tip }) {
  const pruhy = { emerald: "bg-emerald-500", red: "bg-rose-500" };
  const barvy = { emerald: "text-emerald-700", red: "text-rose-700" };
  const castka = (n) => {
    if (!signed) return `${fmt(Math.abs(n))} Kč`;
    return `${n < 0 ? "−" : "+"}${fmt(Math.abs(n))} Kč`;
  };
  const cislo = (n) => (signed ? `${n < 0 ? "−" : "+"}${fmt(Math.abs(n))}` : fmt(Math.abs(n)));

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className={`h-1.5 ${pruhy[color] || pruhy.emerald}`} />
      <div className="p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <span className="flex min-w-[7rem] flex-1 items-center gap-1 text-[11px] font-bold uppercase leading-tight tracking-wider text-slate-400">
            <span>{label}</span>
            {tip && <InfoTip text={tip} />}
          </span>
          <span className={`ml-auto shrink-0 whitespace-nowrap text-xl font-bold tabular-nums sm:text-lg lg:text-xl ${barvy[color] || barvy.emerald}`}>
            {castka(ytd)}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
            Ø měsíc {cislo(avg)}
          </span>
          {eoy !== null && eoy !== undefined && (
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
              odhad roku {cislo(eoy)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// === TREND TABLE — scoreboard každý měsíc na vlastním řádku, max přehlednost ===
// === DAILY CASHFLOW CHART — denní rozpad za vybraný měsíc ===
// Hlavní AREA chart = kumulativní Op. Result od 1. dne (jak měsíc postupně narůstá do plus/minus).
// Pod ním mini bary per day = NET dne (zelený když přidal, červený když ubral).
// Plus best/worst day callouty.
function DailyCashflowChart({ data, summary, blurred }) {
  const fmtK = (v) => {
    if (v == null) return "0";
    const abs = Math.abs(v);
    if (abs >= 1000000) return `${(v / 1000000).toFixed(2)}M`;
    if (abs >= 1000) return `${Math.round(v / 1000)}k`;
    return String(Math.round(v));
  };
  const fmtKc = (v) => `${(v >= 0 ? "+" : "−")}${new Intl.NumberFormat("cs-CZ").format(Math.abs(Math.round(v || 0)))} Kč`;

  // Připrav data: NET dne (revenue + expenses, kde expenses jsou už záporné), kumulativní opResult
  // cumPos / cumNeg = pre-computed pole pro split area chart (zelený nahoru, červený dolů)
  const chartData = data.map((d) => ({
    dayNum: d.dayNum,
    netDay: (d.revenue || 0) + (d.expenses || 0), // NET = jak moc se za daný den zlepšilo/zhoršilo
    revenue: d.revenue || 0,
    expenses: d.expenses || 0,
    cumOpResult: d.cumOpResult,
    cumPos: Math.max(0, d.cumOpResult || 0),
    cumNeg: Math.min(0, d.cumOpResult || 0),
    opResult: d.opResult,
    count: d.count,
  }));

  const opPositive = (summary?.opResult || 0) >= 0;
  const noData = !summary || summary.txCount === 0;

  // Best / worst day (jen dny s transakcemi)
  const daysWithTx = chartData.filter((d) => d.count > 0);
  const bestDay = daysWithTx.length ? daysWithTx.reduce((a, b) => (b.netDay > a.netDay ? b : a), daysWithTx[0]) : null;
  const worstDay = daysWithTx.length ? daysWithTx.reduce((a, b) => (b.netDay < a.netDay ? b : a), daysWithTx[0]) : null;

  // Aktivní den (poslední s transakcí — tj. "kde aktuálně jsme")
  const lastActiveIdx = (() => {
    for (let i = chartData.length - 1; i >= 0; i--) if (chartData[i].count > 0) return i;
    return -1;
  })();

  return (
    <>
      {/* 13.9.2026 (kousek 3/7, rozhodnutí Č2): souhrn měsíce je tichý pruh pod
          hlavičkou, ne tři barevné boxy. Ta samá čísla jsou o kousek výš pod
          provozním výsledkem, tady mají jen být po ruce u grafu. Hodnoty jsou
          pilulky jako doplňky jinde v aplikaci. */}
      <div className="grid grid-cols-3 divide-x divide-slate-200 border-b border-slate-200">
        <div className="px-2 py-2.5 text-center sm:px-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Příjmy</div>
          <span className={`mt-1 inline-flex whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold tabular-nums text-emerald-700 sm:px-2.5 sm:text-sm ${blurred ? "demo-photo" : ""}`}>
            +{fmtK(summary?.revenue || 0)} Kč
          </span>
        </div>
        <div className="px-2 py-2.5 text-center sm:px-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Výdaje</div>
          <span className={`mt-1 inline-flex whitespace-nowrap rounded-full bg-rose-50 px-2 py-0.5 text-xs font-bold tabular-nums text-rose-700 sm:px-2.5 sm:text-sm ${blurred ? "demo-photo" : ""}`}>
            −{fmtK(Math.abs(summary?.expenses || 0))} Kč
          </span>
        </div>
        <div className="px-2 py-2.5 text-center sm:px-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{opPositive ? "Zisk" : "Ztráta"}</div>
          <span className={`mt-1 inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold tabular-nums sm:px-2.5 sm:text-sm ${opPositive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"} ${blurred ? "demo-photo" : ""}`}>
            {opPositive ? "+" : "−"}{fmtK(Math.abs(summary?.opResult || 0))} Kč
          </span>
        </div>
      </div>

      {noData ? (
        <div className="px-4 py-10 text-center text-sm text-slate-400 sm:px-5">
          Žádné transakce v tomto měsíci.
        </div>
      ) : (
        <>
          <SkupinaPrehledu ikona={TrendingUp} nazev="Kumulativní výsledek od 1. dne" />
          <div className="p-3 sm:p-4">
            <div className={`h-48 sm:h-56 w-full ${blurred ? "demo-photo" : ""}`}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="cumPos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="cumNeg" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" vertical={false} />
                  <XAxis
                    dataKey="dayNum"
                    stroke="#786786"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    interval={0}
                    tickFormatter={(d) => (d % 5 === 0 || d === 1 ? String(d) : "")}
                  />
                  <YAxis stroke="#786786" tick={{ fontSize: 10 }} tickFormatter={fmtK} width={48} />
                  <ReferenceLine y={0} stroke="#786786" strokeWidth={1} />
                  <RTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const d = payload[0]?.payload;
                      if (!d) return null;
                      return (
                        <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2 text-xs">
                          <div className="font-bold text-slate-700 mb-1">Den {label}.</div>
                          <div className="space-y-0.5 tabular-nums">
                            <div className="text-slate-700 font-bold pb-1 border-b border-slate-100">
                              Kumulativně: {fmtKc(d.cumOpResult)}
                            </div>
                            <div className="text-slate-600 pt-1">Změna za den: <b>{fmtKc(d.netDay)}</b></div>
                            <div className="text-emerald-700">Příjmy: {fmtKc(d.revenue)}</div>
                            <div className="text-red-700">Výdaje: {fmtKc(d.expenses)}</div>
                            {d.count > 0 && <div className="text-slate-400 text-[11px] pt-0.5">{d.count} {d.count === 1 ? "transakce" : d.count < 5 ? "transakce" : "transakcí"}</div>}
                          </div>
                        </div>
                      );
                    }}
                  />
                  {/* Dvě area: pozitivní část = zelená nahoru, negativní = červená dolů.
                      Pre-computed pole cumPos/cumNeg ať to fungovalo i ve starších recharts. */}
                  <Area
                    type="monotone"
                    dataKey="cumPos"
                    stroke="none"
                    fill="url(#cumPos)"
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumNeg"
                    stroke="none"
                    fill="url(#cumNeg)"
                    isAnimationActive={false}
                  />
                  {/* Hlavní čára přes celou trajektorii */}
                  <Line
                    type="monotone"
                    dataKey="cumOpResult"
                    stroke={opPositive ? "#059669" : "#dc2626"}
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 5, strokeWidth: 2, fill: "#fff" }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <SkupinaPrehledu
            ikona={Activity}
            nazev="Co se stalo den po dni"
            napoveda="Net dne je rozdíl příjmů a výdajů daného dne. Zelený sloupec znamená, že den přidal, červený, že ubral."
          />
          <div className="p-3 sm:p-4">
            <div className={`h-28 sm:h-32 w-full ${blurred ? "demo-photo" : ""}`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" vertical={false} />
                  <XAxis
                    dataKey="dayNum"
                    stroke="#786786"
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    interval={0}
                    tickFormatter={(d) => (d % 5 === 0 || d === 1 ? String(d) : "")}
                  />
                  <YAxis stroke="#786786" tick={{ fontSize: 10 }} tickFormatter={fmtK} width={48} />
                  <ReferenceLine y={0} stroke="#786786" strokeWidth={1} />
                  <RTooltip
                    cursor={{ fill: "rgba(99, 102, 241, 0.05)" }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      const d = payload[0]?.payload;
                      if (!d) return null;
                      const sign = d.netDay >= 0 ? "+" : "−";
                      return (
                        <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-2.5 py-1.5 text-xs">
                          <div className="font-bold text-slate-700">Den {label}.</div>
                          <div className={`font-bold tabular-nums ${d.netDay >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {sign}{fmtK(Math.abs(d.netDay))} Kč
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="netDay" radius={[3, 3, 3, 3]} maxBarSize={26}>
                    {chartData.map((d, i) => (
                      <Cell
                        key={i}
                        fill={d.netDay >= 0 ? "#10b981" : "#ef4444"}
                        opacity={i === lastActiveIdx ? 1 : 0.85}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Nejlepší a nejhorší den */}
          {(bestDay || worstDay) && (
            <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4">
              {bestDay && bestDay.netDay > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 text-white font-black flex items-center justify-center text-sm shadow">
                    {bestDay.dayNum}.
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Nejlepší den</div>
                    <div className={`text-base font-bold tabular-nums text-emerald-800 ${blurred ? "demo-photo" : ""}`}>
                      +{fmtK(bestDay.netDay)} Kč
                    </div>
                  </div>
                </div>
              )}
              {worstDay && worstDay.netDay < 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-red-500 text-white font-black flex items-center justify-center text-sm shadow">
                    {worstDay.dayNum}.
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-red-700 font-bold">Nejhorší den</div>
                    <div className={`text-base font-bold tabular-nums text-red-800 ${blurred ? "demo-photo" : ""}`}>
                      −{fmtK(Math.abs(worstDay.netDay))} Kč
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

function TrendChart({ data }) {
  const uzka = useUzkaObrazovka();
  const fmtK = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1000000) return `${(v / 1000000).toFixed(2)}M`;
    if (abs >= 1000) return `${Math.round(v / 1000)}k`;
    return String(Math.round(v));
  };

  // Max abs hodnota pro škálování barů (Revenue i Expenses)
  const maxBar = Math.max(...data.flatMap((d) => [d.revenue || 0, Math.abs(d.expenses || 0)]), 1);

  return (
    <div className="space-y-1.5">
      {/* Hlavička sloupců. Na telefonu není co popisovat, tam je z řádku karta. */}
      {!uzka && (
        <div className="grid grid-cols-12 gap-2 px-2 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
          <div className="col-span-1">Měsíc</div>
          <div className="col-span-4">Příjmy a výdaje</div>
          <div className="col-span-3 text-center">Operating Result</div>
          <div className="col-span-2 text-center">Cash flow</div>
          <div className="col-span-2 text-right">Trend MoM</div>
        </div>
      )}

      {data.map((d, i) => {
        const opPositive = d.opResult >= 0;
        const opBg = opPositive ? "bg-emerald-100" : "bg-red-100";
        const opText = opPositive ? "text-emerald-800" : "text-red-800";
        const cashFlow = d.cashFlow != null ? d.cashFlow : d.opResult;
        const cashPositive = cashFlow >= 0;
        const cashHasDiff = Math.abs(cashFlow - d.opResult) > 100;

        // MoM trend
        const prev = i > 0 ? data[i - 1] : null;
        let momDelta = null;
        if (prev && prev.opResult != null) momDelta = d.opResult - prev.opResult;

        // Bar widths (jako % z maxBar)
        const revPct = Math.round((d.revenue / maxBar) * 100);
        const expPct = Math.round((Math.abs(d.expenses) / maxBar) * 100);

        if (uzka) {
          // Telefon: měsíc a výsledek na první řádek, pruhy pod ně, cash flow
          // a meziměsíční změna jako štítky. Šest sloupců vedle sebe se na
          // 390px zúžilo tak, že se lámalo i „Kč".
          return (
            <div key={d.mk} className="polozka">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-bold text-slate-900">{MONTH_FULL[parseInt(d.mk.split("-")[1], 10) - 1]}</div>
                  <div className="text-[11px] tabular-nums text-slate-400">{d.mk}</div>
                </div>
                <div className={`shrink-0 rounded-xl px-3 py-1.5 text-right ${opBg}`}>
                  <div className={`text-xl font-black tabular-nums ${opText}`}>
                    {opPositive ? "+" : "−"}{fmtK(Math.abs(d.opResult))} Kč
                  </div>
                  <div className={`text-[11px] font-bold uppercase tracking-wider ${opText} opacity-80`}>
                    {opPositive ? "Zisk" : "Ztráta"}
                  </div>
                </div>
              </div>

              <div className="mt-2.5 space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-8 text-[11px] font-bold text-slate-500">Rev</div>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${revPct}%` }} />
                  </div>
                  <div className="w-20 text-right text-sm font-bold tabular-nums text-emerald-700">+{fmtK(d.revenue)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 text-[11px] font-bold text-slate-500">Exp</div>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-red-500" style={{ width: `${expPct}%` }} />
                  </div>
                  <div className="w-20 text-right text-sm font-bold tabular-nums text-red-700">−{fmtK(Math.abs(d.expenses))}</div>
                </div>
              </div>

              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-bold ${
                  cashHasDiff ? (cashPositive ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-700") : "bg-slate-100 text-slate-600"
                }`}>
                  {cashHasDiff ? "Vč. půjček" : "Cash flow"} {cashPositive ? "+" : "−"}{fmtK(Math.abs(cashFlow))}
                </span>
                {momDelta !== null && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-bold ${
                    momDelta >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                  }`}>
                    {momDelta >= 0 ? "▲" : "▼"} {fmtK(Math.abs(momDelta))} Kč vs {prev?.label}
                  </span>
                )}
              </div>
            </div>
          );
        }

        return (
          <div key={d.mk} className="grid grid-cols-12 gap-2 items-center bg-white border border-slate-200 rounded-xl px-3 py-2.5 hover:bg-slate-50 transition-colors">
            {/* MĚSÍC */}
            <div className="col-span-1">
              <div className="text-base font-bold text-slate-800">{d.label}</div>
              <div className="text-[11px] text-slate-400">{d.mk}</div>
            </div>

            {/* PŘÍJMY A VÝDAJE — dva mini bary nad sebou */}
            <div className="col-span-4 space-y-1">
              <div className="flex items-center gap-2">
                <div className="text-[11px] text-slate-500 w-7">Rev</div>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${revPct}%` }} />
                </div>
                <div className="text-sm font-bold text-emerald-700 tabular-nums w-20 text-right">+{fmtK(d.revenue)}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-[11px] text-slate-500 w-7">Exp</div>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500 rounded-full transition-all" style={{ width: `${expPct}%` }} />
                </div>
                <div className="text-sm font-bold text-red-700 tabular-nums w-20 text-right">−{fmtK(Math.abs(d.expenses))}</div>
              </div>
            </div>

            {/* OP RESULT — barevná dlaždice */}
            <div className={`col-span-3 ${opBg} rounded-xl px-3 py-2 text-center`}>
              <div className={`text-2xl font-black tabular-nums ${opText}`}>
                {opPositive ? "+" : "−"}{fmtK(Math.abs(d.opResult))} <span className="text-base opacity-70">Kč</span>
              </div>
              <div className={`text-[11px] uppercase tracking-wider mt-0.5 ${opText} opacity-80`}>
                {opPositive ? "ZISK" : "ZTRÁTA"}
              </div>
            </div>

            {/* CASH FLOW */}
            <div className={`col-span-2 rounded-xl px-2.5 py-2 text-center ${cashHasDiff ? (cashPositive ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200") : "bg-slate-50 border border-slate-100"}`}
                 title={cashHasDiff ? "Cash flow se liší od Op Result kvůli půjčkám/převodům" : "Cash flow = Op Result (žádné půjčky/převody)"}>
              <div className={`text-base font-bold tabular-nums ${cashPositive ? "text-emerald-700" : "text-red-700"}`}>
                {cashPositive ? "+" : "−"}{fmtK(Math.abs(cashFlow))}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">
                {cashHasDiff ? "vč. půjček" : "= Op"}
              </div>
            </div>

            {/* MoM TREND */}
            <div className="col-span-2 text-right">
              {momDelta !== null ? (
                <div className={`text-sm font-bold tabular-nums ${momDelta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {momDelta >= 0 ? "▲" : "▼"} {fmtK(Math.abs(momDelta))} Kč
                  <div className="text-[11px] text-slate-500 font-normal">vs {prev?.label}</div>
                </div>
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// === BULK CATEGORY PICKER — kompaktní výběr kategorie pro hromadnou klasifikaci ===
function BulkCategoryPicker({ categories, onPick, onCancel }) {
  const [search, setSearch] = useState("");
  const flatList = useMemo(() => {
    const q = search.trim().toLowerCase();
    const seen = new Set();
    const out = [];
    for (const c of categories) {
      const l1 = c.kategorie_l1 || "Ostatní";
      const l2 = c.kategorie_l2 || "";
      const l3 = c.kategorie_l3 || "";
      const key = `${l1}|${l2}|${l3}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (q && !`${l1} ${l2} ${l3}`.toLowerCase().includes(q)) continue;
      out.push({ l1, l2, l3 });
    }
    out.sort((a, b) => {
      const ia = L1_ORDER.indexOf(a.l1), ib = L1_ORDER.indexOf(b.l1);
      const sa = ia === -1 ? 999 : ia;
      const sb = ib === -1 ? 999 : ib;
      if (sa !== sb) return sa - sb;
      return (a.l2 + a.l3).localeCompare(b.l2 + b.l3);
    });
    return out;
  }, [categories, search]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Hledat kategorii…"
          className="field flex-1"
          autoFocus
        />
        <button
          onClick={onCancel}
          className="btn btn-secondary"
        >
          Zrušit
        </button>
      </div>
      {flatList.length === 0 ? (
        <div className="text-center py-3 text-sm text-slate-500 italic">Nic pro „{search}"</div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto bg-white rounded-xl border border-slate-200">
          {(() => {
            const out = [];
            let lastL1 = null;
            for (let i = 0; i < flatList.length; i++) {
              const item = flatList[i];
              const c = L1_COLOR[item.l1] || L1_COLOR[""];
              if (item.l1 !== lastL1) {
                out.push(
                  <div key={`hdr-${item.l1}`} className={`sticky top-0 z-10 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold ${c.solid} text-white`}>
                    {item.l1}
                  </div>
                );
                lastL1 = item.l1;
              }
              out.push(
                <button
                  key={`row-${i}`}
                  onClick={() => onPick(item.l1, item.l2, item.l3)}
                  className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-b-0 transition-colors"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${c.solid} shrink-0`} />
                  <span className="text-sm text-slate-700 truncate">
                    <span className="text-slate-500">{item.l2}</span>
                    {item.l3 && (
                      <>
                        <span className="text-slate-400 mx-1">/</span>
                        <span className="font-medium text-slate-800">{item.l3}</span>
                      </>
                    )}
                  </span>
                </button>
              );
            }
            return out;
          })()}
        </div>
      )}
    </div>
  );
}


// === RULES CARD — collapsible drop down s bulk select + delete + filter podle typu + směru ===
function RulesCard({ rules, selectedRuleIds, toggleRuleSelected, onBulkDelete, onSingleDelete, onSelectAll, onClearSelection }) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterTyp, setFilterTyp] = useState("all");
  const [filterDir, setFilterDir] = useState("all"); // all / kredit / debet
  const allActive = rules.filter((r) => !r.zruseno_kdy);

  const sortedAll = [...allActive].sort((a, b) => {
    const ta = a.vytvoreno_kdy || "";
    const tb = b.vytvoreno_kdy || "";
    if (ta !== tb) return tb.localeCompare(ta);
    return (a.pattern_hodnota || "").localeCompare(b.pattern_hodnota || "");
  });

  // Direction normalizace — prázdné = "any"
  const ruleDir = (r) => {
    const d = (r.direction || "").trim().toLowerCase();
    return d === "kredit" || d === "debet" ? d : "any";
  };

  // Aplikuj OBA filtry (AND)
  const active = sortedAll.filter((r) => {
    if (filterTyp !== "all" && r.pattern_typ !== filterTyp) return false;
    if (filterDir !== "all") {
      const d = ruleDir(r);
      // "any" pravidla zobrazujeme v obou skupinách (matchnou kredit i debet)
      if (d !== filterDir && d !== "any") return false;
    }
    return true;
  });

  // Counters
  const typCounts = allActive.reduce((acc, r) => {
    acc[r.pattern_typ] = (acc[r.pattern_typ] || 0) + 1;
    return acc;
  }, {});
  const dirCounts = allActive.reduce((acc, r) => {
    const d = ruleDir(r);
    if (d === "any") { acc.kredit = (acc.kredit || 0) + 1; acc.debet = (acc.debet || 0) + 1; }
    else acc[d] = (acc[d] || 0) + 1;
    return acc;
  }, {});

  const allSelected = active.length > 0 && active.every((r) => selectedRuleIds.has(r.id));

  const typLabel = (t) => ({
    merchant: "Merchant",
    protiucet_nazev: "Jméno",
    protiucet_cislo: "Účet",
    vs: "VS",
    castka_czk: "Částka",
    fapi_service: "FAPI služba",
  }[t] || t);

  // === Detekce duplicitních / překrývajících se pravidel ===
  // 1) EXACT duplicity: stejný typ + hodnota (normalizováno) + direction → ponecháme nejnovější,
  //    starší navrhneme ke smazání.
  // 2) OVERLAP: stejný typ + direction, ale jedna hodnota je substring druhé (např. "Alza" vs
  //    "Alza.cz a.s."). Obě se spustí → zbytečná práce. Navrhneme smazat SPECIFICKOU variantu
  //    (delší) a ponechat obecnější (kratší) — ten chytne víc případů.
  const normHodnota = (s) => String(s || "").toLowerCase().trim();
  const duplicateAnalysis = useMemo(() => {
    const exactDupToDelete = []; // ids starších duplicitů
    const overlapToDelete = []; // ids delších specifických variant
    const groups = {};
    for (const r of allActive) {
      const key = `${r.pattern_typ}|${ruleDir(r)}|${normHodnota(r.pattern_hodnota)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    for (const key in groups) {
      const g = groups[key];
      if (g.length <= 1) continue;
      // Seřaď od nejnovějšího — prvý zůstane, ostatní smazat
      const sorted = [...g].sort((a, b) => (b.vytvoreno_kdy || "").localeCompare(a.vytvoreno_kdy || ""));
      for (let i = 1; i < sorted.length; i++) exactDupToDelete.push(sorted[i].id);
    }

    // Overlap detekce (jen pro textové typy — merchant, protiucet_nazev, fapi_service)
    const textTypes = new Set(["merchant", "protiucet_nazev", "fapi_service"]);
    for (const a of allActive) {
      if (!textTypes.has(a.pattern_typ)) continue;
      if (exactDupToDelete.includes(a.id)) continue;
      const aVal = normHodnota(a.pattern_hodnota);
      if (aVal.length < 3) continue;
      for (const b of allActive) {
        if (a.id === b.id) continue;
        if (b.pattern_typ !== a.pattern_typ) continue;
        if (ruleDir(a) !== ruleDir(b)) continue;
        const bVal = normHodnota(b.pattern_hodnota);
        if (bVal.length < 3) continue;
        // b obsahuje a (b je delší/specifičtější) → b je redundantní (a ho chytne taky)
        if (bVal.length > aVal.length && bVal.includes(aVal)) {
          if (!overlapToDelete.includes(b.id) && !exactDupToDelete.includes(b.id)) {
            overlapToDelete.push(b.id);
          }
        }
      }
    }

    return {
      exactDuplicates: exactDupToDelete,
      overlapping: overlapToDelete,
      total: exactDupToDelete.length + overlapToDelete.length,
    };
  }, [allActive]);

  const selectProblems = () => {
    const ids = [...duplicateAnalysis.exactDuplicates, ...duplicateAnalysis.overlapping];
    // Reset aktuálního výběru, pak přidej problémové
    onClearSelection();
    ids.forEach((id) => { if (!selectedRuleIds.has(id)) toggleRuleSelected(id); });
    if (!isOpen) setIsOpen(true);
  };

  // 13.9.2026 (kousek 7/7, rozhodnutí Č1 a C): karta ze vzorníku. Dřív byl bílý
  // box, v něm dvě řady barevných štítků a pravidlo jako jedna linka se sedmi
  // údaji v jedenáctibodovém písmu a emoji obálkou. Teď je pravidlo dvouřádkové
  // s pilulkami a úklid duplicit je světlý pruh Úklid, ne žlutá hláška.
  const mnozstviPravidel = (n) => (n === 1 ? "pravidlo dělá stejnou práci" : n < 5 ? "pravidla dělají stejnou práci" : "pravidel dělá stejnou práci");

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={SlidersHorizontal}
        nazev="Pravidla"
        podtitulek={`${active.length} aktivních`}
        napoveda="Pravidla pro automatické zařazování. Když zařadíš transakci, vznikne pravidlo, podle čeho se poznala a kam patří. Při dalším importu i při zařazení v editoru se stejné transakce zařadí samy, ve všech měsících. Zaškrtávátky můžeš smazat víc pravidel najednou."
        odznak={duplicateAnalysis.total > 0 ? `${duplicateAnalysis.total} k úklidu` : false}
        odznakTon="zluty"
        sbalitelne
        otevreno={isOpen}
        onToggle={() => setIsOpen((o) => !o)}
      />

      {isOpen && (
        <>
          {/* Úklid duplicitních a překrývajících se pravidel */}
          {duplicateAnalysis.total > 0 && (
            <>
              <SkupinaPrehledu ikona={Sparkles} nazev="Úklid" />
              <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800">
                    {duplicateAnalysis.total} {mnozstviPravidel(duplicateAnalysis.total)}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {duplicateAnalysis.exactDuplicates.length > 0 && (
                      <Pilulka>{duplicateAnalysis.exactDuplicates.length}× duplicitní</Pilulka>
                    )}
                    {duplicateAnalysis.overlapping.length > 0 && (
                      <Pilulka>{duplicateAnalysis.overlapping.length}× zbytečně přesné</Pilulka>
                    )}
                  </div>
                </div>
                <button
                  onClick={selectProblems}
                  className="btn btn-primary shrink-0"
                  title="Označí duplicitní a zbytečně přesná pravidla. Pak je smažeš tlačítkem Smazat."
                >
                  Označit
                </button>
              </div>
            </>
          )}

          {allActive.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">
              Zatím žádná pravidla, vzniknou samy, jakmile zařadíš první transakci.
            </div>
          ) : (
            <>
              {/* Filtry podle směru a podle toho, podle čeho pravidlo hledá */}
              <div className="space-y-2 border-b border-slate-200 bg-brand-light/60 p-3 sm:p-4">
                <RozbalovaciVyber
                  stitek="Směr"
                  aria-label="Filtr pravidel podle směru"
                  value={filterDir}
                  onChange={setFilterDir}
                  items={[
                    { key: "all", label: `Všechny (${allActive.length})` },
                    { key: "kredit", label: `Příjmy (${dirCounts.kredit || 0})` },
                    { key: "debet", label: `Výdaje (${dirCounts.debet || 0})` },
                  ]}
                />
                <RozbalovaciVyber
                  stitek="Podle"
                  aria-label="Filtr pravidel podle typu"
                  value={filterTyp}
                  onChange={setFilterTyp}
                  items={[
                    { key: "all", label: `Všechno (${allActive.length})` },
                    ...["merchant", "protiucet_nazev", "protiucet_cislo", "vs", "castka_czk", "fapi_service"]
                      .filter((t) => typCounts[t] > 0)
                      .map((t) => ({ key: t, label: `${typLabel(t)} (${typCounts[t]})` })),
                  ]}
                />
              </div>

              {/* Hromadný výběr, stejný pruh jako u transakcí */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 sm:px-4">
                <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs font-semibold text-slate-600">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = !allSelected && selectedRuleIds.size > 0; }}
                    onChange={() => {
                      if (allSelected) onClearSelection();
                      else active.forEach((r) => { if (!selectedRuleIds.has(r.id)) toggleRuleSelected(r.id); });
                    }}
                    className="h-4 w-4 accent-brand"
                  />
                  {selectedRuleIds.size > 0 ? `${selectedRuleIds.size} vybráno` : `Vybrat vše (${active.length})`}
                </label>
                {selectedRuleIds.size > 0 && (
                  <button onClick={onBulkDelete} className="btn btn-danger">
                    <Trash2 className="h-3.5 w-3.5" /> Smazat ({selectedRuleIds.size})
                  </button>
                )}
              </div>

              <div className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
                {active.map((r) => {
                  const sel = selectedRuleIds.has(r.id);
                  const d = ruleDir(r);
                  const smer = d === "kredit"
                    ? { label: "Příjmy", cls: "bg-emerald-100 text-emerald-700" }
                    : d === "debet"
                    ? { label: "Výdaje", cls: "bg-rose-100 text-rose-700" }
                    : { label: "Příjmy i výdaje", cls: "bg-slate-100 text-slate-600" };
                  return (
                    <div key={r.id} className={`flex items-start gap-3 px-3 py-2.5 sm:px-4 ${sel ? "bg-rose-50" : ""}`}>
                      <input
                        type="checkbox"
                        checked={sel}
                        onChange={() => toggleRuleSelected(r.id)}
                        className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-brand"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="min-w-0 truncate font-mono text-sm font-semibold text-slate-800 demo-sensitive" title={r.pattern_hodnota}>
                            {r.pattern_hodnota}
                          </span>
                          <button
                            onClick={() => onSingleDelete(r.id)}
                            className="shrink-0 p-1 text-slate-400 sm:hover:text-rose-600"
                            title="Zrušit pravidlo"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${smer.cls}`}>
                            {smer.label}
                          </span>
                          <Pilulka>{typLabel(r.pattern_typ)}</Pilulka>
                          <Pilulka>
                            <span className="truncate" title={`${r.kategorie_l1} / ${r.kategorie_l2}${r.kategorie_l3 ? ` / ${r.kategorie_l3}` : ""}`}>
                              {r.kategorie_l3 || r.kategorie_l2}
                            </span>
                          </Pilulka>
                          <Pilulka><span title={`Pravidlo se použilo ${r.confidence}×`}>{r.confidence}×</span></Pilulka>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </KartaPrehledu>
  );
}

// === P&L GROUP — collapsible L1 → L2 → L3 ===
function PnLGroup({ group, maxTotal = 0, categories, onClassify, fapiInvoices, editingTxId, setEditingTxId }) {
  const [openL1, setOpenL1] = useState(false); // skupina defaultně sbalená
  const [openL2, setOpenL2] = useState(() => new Set()); // podkategorie default sbalené
  const kladny = group.total > 0;
  const sign = group.total > 0 ? "+" : group.total < 0 ? "−" : "";
  const numColor = group.total > 0 ? "text-emerald-700" : group.total < 0 ? "text-rose-700" : "text-slate-500";
  const podil = maxTotal > 0 ? Math.max(2, Math.round((Math.abs(group.total) / maxTotal) * 100)) : 0;

  const l2Entries = Object.values(group.l2s).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));

  const toggleL2 = (l2) => {
    setOpenL2((s) => {
      const next = new Set(s);
      if (next.has(l2)) next.delete(l2);
      else next.add(l2);
      return next;
    });
  };

  return (
    <div className="px-4 py-3 sm:px-5">
      {/* Řádek skupiny: název, částka, pod tím proužek podílu a počet plateb */}
      <button onClick={() => setOpenL1((o) => !o)} className="w-full text-left">
        <div className="flex items-baseline justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${openL1 ? "" : "-rotate-90"}`} />
            <span className="truncate text-sm font-bold text-slate-800">{group.l1}</span>
          </span>
          <span className={`shrink-0 whitespace-nowrap text-base font-bold tabular-nums ${numColor}`}>
            {sign}{fmt(Math.abs(group.total))} Kč
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
            <span
              className={`block h-full rounded-full ${kladny ? "bg-emerald-500" : "bg-rose-500"}`}
              style={{ width: `${podil}%` }}
            />
          </span>
          <Pilulka>{group.count} {group.count === 1 ? "platba" : group.count < 5 ? "platby" : "plateb"}</Pilulka>
        </div>
      </button>

      {/* Podkategorie */}
      {openL1 && (
        <div className="mt-2 overflow-hidden rounded-xl border border-slate-200">
          <SkupinaPrehledu ikona={List} nazev="Podkategorie" />
          {l2Entries.length === 0 ? (
            <div className="py-2 text-center text-xs text-slate-400">Žádné položky</div>
          ) : (
            l2Entries.map((l2g) => {
              const l2sign = l2g.total > 0 ? "+" : l2g.total < 0 ? "−" : "";
              const l2color = l2g.total > 0 ? "text-emerald-700" : l2g.total < 0 ? "text-rose-700" : "text-slate-500";
              const isOpen = openL2.has(l2g.l2);
              // Transakce v podkategorii, seřazené podle absolutní částky, pro analýzu a přeřazení.
              const txs = (l2g.txs || []).slice().sort((a, b) => {
                const av = Math.abs(parseFloat(String(a.castka_czk).replace(/[ \u00a0]/g, "").replace(",", ".")) || 0);
                const bv = Math.abs(parseFloat(String(b.castka_czk).replace(/[ \u00a0]/g, "").replace(",", ".")) || 0);
                return bv - av;
              });
              const hasTx = txs.length > 0;
              const canRetag = !!(categories && onClassify);

              return (
                <div key={l2g.l2} className="border-t border-slate-100">
                  <button
                    onClick={() => hasTx && toggleL2(l2g.l2)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 ${hasTx ? "cursor-pointer sm:hover:bg-slate-50" : "cursor-default"}`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {hasTx ? (
                        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                      ) : (
                        <span className="h-3.5 w-3.5 shrink-0" />
                      )}
                      <span className="truncate text-sm text-slate-700">{l2g.l2}</span>
                      <Pilulka>{l2g.count}</Pilulka>
                    </span>
                    <span className={`shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums ${l2color}`}>
                      {l2sign}{fmt(Math.abs(l2g.total))} Kč
                    </span>
                  </button>

                  {/* Jednotlivé transakce v podkategorii — klik na řádek = přetagovat (pokud je ve špatné kategorii) */}
                  {isOpen && hasTx && (
                    <div className="bg-slate-50/60 border-t border-slate-100 px-2 py-2 space-y-1.5">
                      {canRetag ? (
                        txs.map((t) => (
                          <TransactionRow
                            key={t.id}
                            tx={t}
                            categories={categories}
                            onClassify={onClassify}
                            fapiInvoices={fapiInvoices}
                            editing={editingTxId === t.id}
                            onToggleEdit={() => setEditingTxId && setEditingTxId(editingTxId === t.id ? null : t.id)}
                            onCloseEdit={() => setEditingTxId && setEditingTxId(null)}
                            selected={false}
                            onToggleSelected={null}
                          />
                        ))
                      ) : (
                        txs.map((t) => {
                          const amt = parseFloat(String(t.castka_czk).replace(/[  ]/g, "").replace(",", ".")) || 0;
                          const sgn = t.typ === "kredit" ? "+" : "−";
                          const col = t.typ === "kredit" ? "text-emerald-700" : "text-red-700";
                          return (
                            <div key={t.id} className="flex items-center justify-between gap-2 px-2 py-1 text-xs">
                              <div className="text-slate-600 truncate">{t.merchant || t.protiucet_nazev || t.protiucet_cislo || "—"}</div>
                              <div className={`font-semibold tabular-nums whitespace-nowrap ${col}`}>{sgn}{fmt(Math.abs(amt))} Kč</div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// === RESET CONFIRM MODAL — vlastní design pro destruktivní akci ===
// Step 1: výběr rozsahu (vše / konkrétní měsíc) + bullet info
// Step 2: type-to-confirm SMAZAT (přísnější check pro "vše", jeden konfirm pro měsíc)
function ResetConfirmModal({ step, scope, setScope, availableMonths, onClose, onProceedToStep2, onConfirm }) {
  const [typed, setTyped] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (step !== 2) setTyped("");
    if (step === 2 && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [step]);

  const isAll = scope === "all";
  const popisMesice = (mk) => {
    if (!mk || !/^\d{4}-\d{2}$/.test(mk)) return mk;
    const [yy, mm] = mk.split("-");
    const names = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
    return `${names[parseInt(mm, 10) - 1]} ${yy}`;
  };

  // Pro smazání jednoho měsíce není potřeba psát potvrzení, je to méně
  // destruktivní. Pro smazání všeho ano, tam se vypisuje SMAZAT velkým písmem.
  const requireType = isAll;
  const canConfirm = !requireType || typed === "SMAZAT";

  return (
    <Modal
      open={step !== 0}
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={
        <HlavickaOkna
          ikona={AlertTriangle}
          nazev={step === 1 ? "Smazat transakce?" : "Poslední potvrzení"}
          popis="Tuhle akci nejde vzít zpět"
          onClose={onClose}
        />
      }
      footer={
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary">Zrušit</button>
          {step === 1 ? (
            <button onClick={onProceedToStep2} className="btn btn-danger">
              Pokračovat <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={onConfirm} disabled={!canConfirm} className="btn btn-danger">
              <Trash2 className="h-4 w-4" /> {isAll ? "Smazat vše" : `Smazat ${popisMesice(scope)}`}
            </button>
          )}
        </div>
      }
    >
      {step === 1 && (
        <>
          <div className="mb-2 text-sm font-semibold text-slate-700">Co chceš smazat?</div>
          <div className="mb-4 space-y-2">
            {/* Volba: jen konkrétní měsíc */}
            <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${!isAll ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"}`}>
              <input
                type="radio"
                name="resetScope"
                checked={!isAll}
                onChange={() => {
                  const last = availableMonths.length > 0 ? availableMonths[availableMonths.length - 1].mk : "";
                  setScope(last || "all");
                }}
                className="mt-0.5 h-4 w-4 accent-rose-600"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800">Jen jeden měsíc</div>
                <div className="mt-0.5 text-xs text-slate-500">Smaže transakce a jejich zařazení jen za vybraný měsíc.</div>
                {!isAll && availableMonths.length > 0 && (
                  <select
                    value={scope}
                    onChange={(e) => setScope(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="field mt-2 w-full"
                  >
                    {[...availableMonths].reverse().map((m) => (
                      <option key={m.mk} value={m.mk}>{popisMesice(m.mk)} ({m.count} transakcí)</option>
                    ))}
                  </select>
                )}
              </div>
            </label>

            {/* Volba: vše */}
            <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${isAll ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white"}`}>
              <input
                type="radio"
                name="resetScope"
                checked={isAll}
                onChange={() => setScope("all")}
                className="mt-0.5 h-4 w-4 accent-rose-600"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800">Všechny transakce</div>
                <div className="mt-0.5 text-xs text-slate-500">Smaže celou historii, všechny měsíce i roky.</div>
              </div>
            </label>
          </div>

          <Hlaska ton="ok" title="Pravidla zůstanou">
            Při dalším importu se znovu použijí a transakce se zařadí samy.
          </Hlaska>
        </>
      )}

      {step === 2 && (
        <>
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            Chystáš se smazat:{" "}
            <b className="text-rose-700">
              {isAll ? "všechny transakce, všechny měsíce i roky" : `transakce za ${popisMesice(scope)}`}
            </b>
          </div>
          {requireType ? (
            <>
              <div className="mb-3 text-sm text-slate-700">
                Pro potvrzení napiš velkým písmem slovo <code className="rounded bg-rose-50 px-1.5 py-0.5 font-mono font-bold text-rose-700">SMAZAT</code>:
              </div>
              <input
                ref={inputRef}
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && canConfirm) onConfirm(); }}
                placeholder="napiš SMAZAT"
                className={`mb-1 w-full rounded-xl border px-3 py-2.5 font-mono text-base outline-none ${
                  typed === "SMAZAT"
                    ? "border-rose-500 bg-rose-50 text-rose-900"
                    : typed.length > 0
                      ? "border-amber-300 bg-amber-50 text-slate-900"
                      : "border-slate-300 bg-white text-slate-900"
                }`}
              />
            </>
          ) : (
            <div className="text-sm text-slate-700">
              Pokračováním transakce za <b>{popisMesice(scope)}</b> trvale smažeš. Jejich zařazení zmizí, pravidla zůstanou.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
